import React, { useState, useEffect, useRef, Fragment } from "react";
import { ChevronLeft, Star, Check, X, Plus, CheckCircle, Home, ClipboardList, Monitor, MessageSquare, Wallet, RefreshCw, TrendingUp, LogOut, Timer, Eye, Send, ChevronRight, AlertTriangle, Download, Mail, Activity, ShieldCheck, Users, Building2, Ban, Filter } from "lucide-react";
import api, { BASE } from "../api";
import { uploadUrl } from "../uploadUrl";
import { K } from "../theme";
import { Av, Bg, Bt, Cd, Sec, Stat, Tbl, Sl, Inp, Pr, SearchSelect } from "../components";
import { FormModal } from "../components/FormModal";
import { notify, confirmDialog, promptDialog } from "../notify";
import { fmtDMY } from "../dateFormat";

// Module-scope so every component in this file can use it. The exit-interview
// card referenced fmtDate while the only definition lived inside a different
// component further down, which threw "fmtDate is not defined" the moment an
// invite was Pending or Submitted. That local definition still shadows this
// one inside its own component, so nothing else changes.
const fmtDate = (d) => {
  if (!d) return "--";
  try { return fmtDMY(d) || d; }
  catch (e) { return d; }
};

// Evidence / supporting files for an exit (email trail screenshots, notices, a zip of them…).
const fmtSize = (n) => !n ? "" : n < 1024 * 1024 ? `${Math.max(1, Math.round(n / 1024))} KB` : `${(n / 1048576).toFixed(1)} MB`;
const SEP_FILE_HINT = "PDF, images, Word / Excel, .eml / .msg emails, or a zip — up to 15 files, 25 MB each";
const SupportingFilesPicker = ({ files, setFiles, label = "Supporting documents" }) => {
  const ref = useRef(null);
  const add = (list) => {
    const incoming = Array.from(list || []);
    const big = incoming.find(f => f.size > 25 * 1024 * 1024);
    if (big) { notify(`"${big.name}" is over 25 MB`, { title: "File too large", variant: "error" }); return; }
    setFiles(prev => [...prev, ...incoming].slice(0, 15));
    if (ref.current) ref.current.value = "";
  };
  return (
    <div className="mb-3">
      <div className="text-[14px] text-t2 mb-[3px]">{label}</div>
      <div className="p-3 bg-bg2 border border-dashed border-b1 rounded-[10px]">
        <input ref={ref} type="file" multiple className="hidden" onChange={e => add(e.target.files)}
               accept=".pdf,.png,.jpg,.jpeg,.gif,.webp,.bmp,.doc,.docx,.xls,.xlsx,.csv,.txt,.msg,.eml,.zip,.rar,.7z" />
        <Bt v="secondary" s="xs" icon={Plus} onClick={() => ref.current && ref.current.click()}>Add files</Bt>
        <span className="text-[12px] text-t3 ml-2">{SEP_FILE_HINT}</span>
        {files.length > 0 && (
          <div className="mt-2 flex flex-col gap-1">
            {files.map((f, i) => (
              <div key={i} className="flex items-center justify-between gap-2 px-2 py-1 bg-bg1 rounded-md text-[13px]">
                <span className="truncate text-t0">{f.name} <span className="text-t3">{fmtSize(f.size)}</span></span>
                <button type="button" onClick={() => setFiles(prev => prev.filter((_, j) => j !== i))} className="bg-transparent border-0 text-t3 cursor-pointer"><X size={13} /></button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
// Termination and absconding exits do not release a Relieving Letter unless HR chooses to.
const NO_RELIEVING_DEFAULT = ["Termination", "Absconding"];
const RelievingChoice = ({ value, onChange }) => (
  <div className="mb-3">
    <div className="text-[14px] text-t2 mb-[3px]">Relieving Letter</div>
    <Sl options={[{ v: "release", l: "Release a Relieving Letter" }, { v: "withhold", l: "Do not release a Relieving Letter" }]} value={value} onChange={onChange} />
    <div className="text-[12px] text-t3 mt-[3px]">{value === "withhold" ? "No Relieving Letter is prepared or emailed. You can change this later from the exit page." : "It is prepared at the Exit In Progress step and emailed only when you press its button."}</div>
  </div>
);
// Uploads the chosen files to a just-created exit. Never throws: the exit already exists by then.
const uploadSepFiles = async (sepId, files) => {
  if (!sepId || !files || !files.length) return;
  try {
    const fd = new FormData(); files.forEach(f => fd.append("files", f));
    await api.upload(`/api/separation/${sepId}/documents`, fd);
  } catch (e) {
    notify((e && e.message ? e.message + " — " : "") + "the exit was created but the files did not upload. Add them from the exit page.", { title: "Files not uploaded", variant: "error" });
  }
};

// Initiate Exit — custom modal so we can react to the employee picker
// and pre-fill notice period from that employee's contract. The flat
// FormModal can't peek into its own field state to do this.
const InitiateExitModal = ({ employees, options, initiateEmpId, setInitiateEmpId, employeeNoticeDays, onClose, onSubmit }) => {
  const [vals, setVals] = useState({
    employee_id: "",
    separation_type: options.separation_types?.[0] || "Resignation",
    reason_category: "",
    reason_detail: "",
    resignation_date: "",
    notice_period_days: "",
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [files, setFiles] = useState([]);
  const [relieving, setRelieving] = useState(null); // null = follow the exit type's default
  const relievingValue = relieving || (NO_RELIEVING_DEFAULT.includes(vals.separation_type) ? "withhold" : "release");

  // Whenever the selected employee changes, reflect their contractual
  // notice period back into the form (and the parent state used by the
  // page-level fetch).
  React.useEffect(() => {
    setInitiateEmpId(vals.employee_id);
  }, [vals.employee_id, setInitiateEmpId]);

  React.useEffect(() => {
    if (employeeNoticeDays != null && !vals.notice_period_days) {
      setVals(p => ({ ...p, notice_period_days: String(employeeNoticeDays) }));
    }
  }, [employeeNoticeDays]); // eslint-disable-line

  const set = (k, v) => setVals(p => ({ ...p, [k]: v }));

  const submit = async () => {
    setErr("");
    if (!vals.employee_id) return setErr("Pick an employee");
    if (!vals.separation_type) return setErr("Separation type is required");
    setSaving(true);
    try {
      await onSubmit({
        ...vals,
        notice_period_days: vals.notice_period_days ? parseInt(vals.notice_period_days) : undefined,
        relieving_letter_decision: relievingValue,
      }, files);
      notify("Exit process initiated", { title: "Saved", variant: "success" });
      onClose();
    } catch (e) {
      setErr(e.message || "Failed");
      notify(e.message || "Failed to initiate exit", { title: "Error", variant: "error" });
    }
    finally { setSaving(false); }
  };

  const lbl = "text-[14px] text-t2 mb-[3px]";
  return (
    <div className="fixed inset-0 animate-fadeIn bg-black/35 z-[200] flex items-center justify-center p-3" onMouseDown={onClose}>
      <div className="animate-modalIn bg-bg1 border border-b1 rounded-2xl p-5 w-full max-w-[712px] max-h-[90vh] overflow-auto" onMouseDown={e => e.stopPropagation()}>
        <div className="flex justify-between items-start mb-4">
          <div className="text-[18px] font-extrabold text-t0">Initiate Exit</div>
          <button onClick={onClose} className="bg-transparent border-0 text-t3 cursor-pointer p-1"><X size={18}/></button>
        </div>

        <div className="mb-3">
          <div className={lbl}>Employee *</div>
          <SearchSelect
            placeholder="Select Employee…"
            searchPlaceholder="Search by name or ID…"
            options={employees.filter(e => e.st !== 'Inactive').map(e => ({
              v: e.id,
              l: e.nm || e.name || e.employee_name || ("EMP-" + e.id),
              sub: [e.id, e.role, e.dept].filter(Boolean).join(' · '),
            }))}
            value={vals.employee_id}
            onChange={v => set('employee_id', v)} />
        </div>

        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <div className={lbl}>Separation Type *</div>
            <Sl options={options.separation_types.map(t => ({ v: t, l: t }))}
                value={vals.separation_type} onChange={v => set('separation_type', v)} />
          </div>
          <div>
            <div className={lbl}>Reason Category</div>
            <Sl options={[{ v: "", l: "Select reason…" }, ...options.reason_categories.map(r => ({ v: r, l: r }))]}
                value={vals.reason_category} onChange={v => set('reason_category', v)} />
          </div>
        </div>

        <div className="mb-3">
          <div className={lbl}>Reason Details</div>
          <textarea value={vals.reason_detail} onChange={e => set('reason_detail', e.target.value)}
                    placeholder="Additional details about the separation…"
                    className="w-full min-w-0 min-h-[50px] p-2 bg-bg2 border border-b0 rounded-lg text-t0 text-[14px] outline-none resize-y" />
        </div>

        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <div className={lbl}>Resignation Date</div>
            <input type="date" value={vals.resignation_date} onChange={e => set('resignation_date', e.target.value)}
                   className="w-full min-w-0 px-[10px] py-[7px] bg-bg2 border border-b0 rounded-[10px] text-[14px] text-t0 outline-none" />
          </div>
          <div>
            <div className={lbl}>Notice Period (days) *</div>
            <input type="number" value={vals.notice_period_days} onChange={e => set('notice_period_days', e.target.value)}
                   placeholder={String(employeeNoticeDays ?? options.notice_period_default ?? 30)}
                   className="w-full min-w-0 px-[10px] py-[7px] bg-bg2 border border-b0 rounded-[10px] text-[14px] text-t0 outline-none" />
            {vals.employee_id && (
              <div className="text-[12px] text-t3 mt-[3px]">
                {employeeNoticeDays != null
                  ? <>Pre-filled from this employee's contract ({employeeNoticeDays}d). Override if needed.</>
                  : <>No contract notice on file — defaulting to company {options.notice_period_default || 30}d.</>}
              </div>
            )}
          </div>
        </div>

        <RelievingChoice value={relievingValue} onChange={setRelieving} />
        <SupportingFilesPicker files={files} setFiles={setFiles}
          label={["Termination", "Absconding"].includes(vals.separation_type) ? "Termination / absconding emails, screenshots or a zip of them" : "Supporting documents (optional)"} />

        {err && <div className="text-[14px] text-r mb-2">{err}</div>}
        <div className="flex gap-2">
          <Bt full onClick={submit} disabled={saving}>{saving ? "Saving…" : "Initiate Exit"}</Bt>
          <Bt v="secondary" full onClick={onClose}>Cancel</Bt>
        </div>
      </div>
    </div>
  );
};

// Employee's tenure as "X year(s) Y month(s)" — the reference's read-only
// "Current Experience" field on the Add Termination form.
const experienceLabel = (joinDate) => {
  if (!joinDate) return "—";
  const start = new Date(joinDate);
  if (isNaN(start.getTime())) return "—";
  const now = new Date();
  let months = (now.getFullYear() - start.getFullYear()) * 12 + (now.getMonth() - start.getMonth());
  if (now.getDate() < start.getDate()) months--;
  if (months < 0) return "—";
  const years = Math.floor(months / 12);
  const rem = months % 12;
  if (years === 0) return `${rem} month${rem === 1 ? "" : "s"}`;
  if (rem === 0) return `${years} year${years === 1 ? "" : "s"}`;
  return `${years} year${years === 1 ? "" : "s"} ${rem} month${rem === 1 ? "" : "s"}`;
};

// "Add Termination" — the form under Track Offboarding's "+" button.
// Unlike Initiate Exit (any separation_type, notice-period-derived LWD),
// this is HR/admin directly closing out a termination: Last working date
// is set explicitly, not computed from notice.
const AddTerminationModal = ({ employees, options, onClose, onSubmit }) => {
  const [vals, setVals] = useState({
    employee_id: "", fnf_eligibility: "", termination_type: "",
    reason_category: "", employee_status_on_exit: "Terminated",
    reason_detail: "", last_working_day: "",
  });
  const [experience, setExperience] = useState(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [files, setFiles] = useState([]);
  const [relieving, setRelieving] = useState("withhold");
  const set = (k, v) => setVals(p => ({ ...p, [k]: v }));

  useEffect(() => {
    if (!vals.employee_id) { setExperience(null); return; }
    api.get("/api/separation/config/options?employee_id=" + encodeURIComponent(vals.employee_id))
      .then(d => setExperience(experienceLabel(d.employee_join_date)))
      .catch(() => setExperience(null));
  }, [vals.employee_id]);

  const submit = async () => {
    setErr("");
    if (!vals.employee_id) return setErr("Employee ID is required");
    if (!vals.termination_type) return setErr("Termination Type is required");
    if (!vals.reason_category) return setErr("Reason is required");
    if (!vals.last_working_day) return setErr("Last working date is required");
    setSaving(true);
    try {
      await onSubmit({
        employee_id: vals.employee_id,
        separation_type: "Termination",
        fnf_eligibility: vals.fnf_eligibility,
        termination_type: vals.termination_type,
        reason_category: vals.reason_category,
        employee_status_on_exit: vals.employee_status_on_exit,
        reason_detail: vals.reason_detail,
        last_working_day: vals.last_working_day,
        relieving_letter_decision: relieving,
      }, files);
      notify("Termination added", { title: "Saved", variant: "success" });
      onClose();
    } catch (e) {
      setErr(e.message || "Failed");
      notify(e.message || "Failed to add termination", { title: "Error", variant: "error" });
    } finally { setSaving(false); }
  };

  const lbl = "text-[14px] text-t2 mb-[3px]";
  return (
    <div className="fixed inset-0 animate-fadeIn bg-black/35 z-[200] flex items-center justify-center p-3" onMouseDown={onClose}>
      <div className="animate-modalIn bg-bg1 border border-b1 rounded-2xl p-5 w-full max-w-[560px] max-h-[90vh] overflow-auto" onMouseDown={e => e.stopPropagation()}>
        <div className="flex justify-between items-start mb-4">
          <div className="text-[18px] font-extrabold text-t0">Add Request</div>
          <button onClick={onClose} className="bg-transparent border-0 text-t3 cursor-pointer p-1"><X size={18} /></button>
        </div>
        <div className="text-[13px] font-bold text-t3 uppercase tracking-wide mb-2">Termination details</div>

        <div className="mb-3">
          <div className={lbl}>Employee ID *</div>
          <SearchSelect
            placeholder="Select Employee…"
            searchPlaceholder="Search by name or ID…"
            options={employees.filter(e => e.st !== 'Inactive').map(e => ({
              v: e.id,
              l: e.nm || e.name || e.employee_name || ("EMP-" + e.id),
              sub: [e.id, e.role, e.dept].filter(Boolean).join(' · '),
            }))}
            value={vals.employee_id}
            onChange={v => set('employee_id', v)} />
        </div>
        <div className="mb-3">
          <div className={lbl}>Current Experience</div>
          <div className="text-[14px] text-t3 italic px-[10px] py-[7px] bg-bg2 border border-b0 rounded-[10px]">{vals.employee_id ? (experience ?? "Loading…") : "—"}</div>
        </div>
        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <div className={lbl}>FNF Eligibility</div>
            <Sl options={[{ v: "", l: "Select…" }, { v: "Yes", l: "Yes" }, { v: "No", l: "No" }]} value={vals.fnf_eligibility} onChange={v => set('fnf_eligibility', v)} />
          </div>
          <div>
            <div className={lbl}>Termination Type: *</div>
            <Sl options={[{ v: "", l: "Select…" }, { v: "Voluntary", l: "Voluntary" }, { v: "Involuntary", l: "Involuntary" }]} value={vals.termination_type} onChange={v => set('termination_type', v)} />
          </div>
        </div>
        <div className="mb-3">
          <div className={lbl}>Reason *</div>
          <Sl options={[{ v: "", l: "Select" }, ...options.reason_categories.map(r => ({ v: r, l: r }))]} value={vals.reason_category} onChange={v => set('reason_category', v)} />
        </div>
        <div className="mb-3">
          <div className={lbl}>Employee Status</div>
          <Sl options={["Terminated", "Resigned", "Not Qualified", "Internship Completed", "Absconded", "Not Active"].map(s => ({ v: s, l: s }))} value={vals.employee_status_on_exit} onChange={v => set('employee_status_on_exit', v)} />
        </div>
        <div className="mb-3">
          <div className={lbl}>Explanation</div>
          <textarea value={vals.reason_detail} onChange={e => set('reason_detail', e.target.value)}
                    placeholder="Details about the termination…"
                    className="w-full min-w-0 min-h-[70px] p-2 bg-bg2 border border-b0 rounded-lg text-t0 text-[14px] outline-none resize-y" />
        </div>
        <div className="mb-3">
          <div className={lbl}>Last working date *</div>
          <input type="date" value={vals.last_working_day} onChange={e => set('last_working_day', e.target.value)}
                 className="w-full min-w-0 px-[10px] py-[7px] bg-bg2 border border-b0 rounded-[10px] text-[14px] text-t0 outline-none" />
        </div>

        <RelievingChoice value={relieving} onChange={setRelieving} />
        <SupportingFilesPicker files={files} setFiles={setFiles} label="Attachment — termination / absconding email, screenshots, or a zip of them" />

        {err && <div className="text-[14px] text-r mb-2">{err}</div>}
        <div className="flex gap-2">
          <Bt full onClick={submit} disabled={saving}>{saving ? "Saving…" : "Submit"}</Bt>
          <Bt v="secondary" full onClick={onClose}>Cancel</Bt>
        </div>
      </div>
    </div>
  );
};

// Admin/HR action to move Last Working Date — most often an early release
// ahead of the default notice period, though any date is accepted. Its own
// dedicated endpoint (not the generic field editor) so it's audited,
// recomputes fnf_due_date, and notifies + emails the employee.
// Exit Interview tab — replaces the old inline star-rating form. The
// exiting employee's login is often already gone by the time this
// matters, so filling it out happens on a public, no-login link
// (PublicExitInterview.jsx) emailed from here — this tab is just
// send / status / read the results.
const ExitInterviewTab = ({ sepId, role, can = () => true }) => {
  const [data, setData] = useState(null); // { questions, invite } | false = error
  const [sending, setSending] = useState(false);
  const canSend = can('separation', 'exit_interview', null, 'edit');

  const load = () => {
    setData(null);
    api.get(`/api/separation/${sepId}/exit-interview`).then(setData).catch(() => setData(false));
  };
  useEffect(load, [sepId]); // eslint-disable-line

  const send = async () => {
    setSending(true);
    try {
      const r = await api.post(`/api/separation/${sepId}/exit-interview/send`, {});
      notify(r.emailed_to ? `Exit interview sent to ${r.emailed_to}` : 'Exit interview link created — no email on file, share it manually.', { title: 'Saved', variant: 'success' });
      load();
    } catch (e) { notify(e.message || 'Failed to send', { title: 'Error', variant: 'error' }); }
    finally { setSending(false); }
  };

  if (data === null) return <div className="text-center p-[30px] text-t3"><Sec icon={MessageSquare} title="Exit Interview" /></div>;
  if (data === false) return <div><Sec icon={MessageSquare} title="Exit Interview" /><div className="text-center p-[30px] text-t3">Couldn't load exit interview status.</div></div>;

  const { questions, invite } = data;

  return (
    <div>
      <Sec icon={MessageSquare} title="Exit Interview" />
      {!invite && (
        <Cd>
          <div className="text-[14px] font-bold text-t0 mb-[6px]">Not sent yet</div>
          <div className="text-[14px] text-t3 mb-4">
            Sends a one-time link by email — no login needed — so the employee can share honest feedback on their own time.
          </div>
          {canSend && <Bt icon={Send} disabled={sending} onClick={send}>{sending ? "Sending…" : "Send Interview"}</Bt>}
        </Cd>
      )}
      {invite && invite.status === 'Pending' && (
        <Cd>
          <div className="text-[14px] font-bold text-t0 mb-[6px]">Awaiting response</div>
          <div className="text-[14px] text-t3 mb-4">
            Sent {fmtDate(invite.created_at)} · link valid until {fmtDate(invite.expires_at)}.
          </div>
          {canSend && <Bt v="secondary" icon={Send} disabled={sending} onClick={send}>{sending ? "Sending…" : "Resend"}</Bt>}
        </Cd>
      )}
      {invite && invite.status === 'Submitted' && (
        <Cd>
          <div className="flex items-center justify-between mb-4">
            <div className="text-[14px] font-bold text-t0">Submitted {fmtDate(invite.submitted_at)}</div>
            <Bg c={K.g} sm dot>Complete</Bg>
          </div>
          <div className="flex flex-col gap-[14px]">
            {questions.map(q => {
              const val = invite.answers?.[q.key];
              return (
                <div key={q.key} className="pb-[14px] border-b border-b0 last:border-0">
                  <div className="text-[14px] font-semibold text-t2 mb-[6px]">{q.label}</div>
                  {q.type === 'rating10' && (
                    <div className="flex items-center gap-[2px]">
                      {Array.from({ length: 10 }, (_, i) => i + 1).map(n => (
                        <Star key={n} size={16} fill={(val || 0) >= n ? K.a : "none"} color={(val || 0) >= n ? K.a : K.t3} />
                      ))}
                      <span className="ml-2 text-[13px] font-bold text-t2">{val}/10</span>
                    </div>
                  )}
                  {q.type === 'yesno' && <Bg c={val === 'Yes' ? K.g : K.r} sm>{val || "—"}</Bg>}
                  {q.type === 'text' && <div className="text-[14px] text-t0 px-[10px] py-[6px] bg-bg2 rounded-lg border border-b0 whitespace-pre-wrap">{val || "—"}</div>}
                </div>
              );
            })}
          </div>
        </Cd>
      )}
    </div>
  );
};

const ChangeReleaseDateModal = ({ sep, onClose, onSaved }) => {
  const [date, setDate] = useState(sep.last_working_day ? String(sep.last_working_day).slice(0, 10) : "");
  const [reason, setReason] = useState("");
  const [replacementRequired, setReplacementRequired] = useState(sep.replacement_required || "Decide later");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  // Same formula the initiate-resignation flow uses: resignation date +
  // notice period — shown so HR can see how far the new date deviates
  // from what the standard notice period would have given.
  const expectedLwd = (() => {
    const start = sep.resignation_date || sep.initiated || sep.created_at;
    if (!start) return null;
    const d = new Date(start);
    d.setDate(d.getDate() + (sep.notice_period_days ?? sep.notice ?? 30));
    return fmtDMY(d);
  })();

  const submit = async () => {
    setErr("");
    if (!date) return setErr("Pick a date");
    setSaving(true);
    try {
      await api.put(`/api/separation/${sep.id}/release-date`, {
        last_working_day: date,
        reason: reason.trim() || undefined,
        replacement_required: replacementRequired,
      });
      notify("Last working date updated", { title: "Saved", variant: "success" });
      onSaved();
    } catch (e) {
      setErr(e.message || "Failed");
      notify(e.message || "Failed to change release date", { title: "Error", variant: "error" });
    } finally { setSaving(false); }
  };

  const lbl = "text-[14px] text-t2 mb-[3px]";
  return (
    <div className="fixed inset-0 animate-fadeIn bg-black/35 z-[200] flex items-center justify-center p-3" onMouseDown={onClose}>
      <div className="animate-modalIn bg-bg1 border border-b1 rounded-2xl p-5 w-full max-w-[480px]" onMouseDown={e => e.stopPropagation()}>
        <div className="flex justify-between items-start mb-4">
          <div className="text-[18px] font-extrabold text-t0">Change Release Date</div>
          <button onClick={onClose} className="bg-transparent border-0 text-t3 cursor-pointer p-1"><X size={18} /></button>
        </div>
        <div className="text-[14px] text-t3 mb-3">
          Current last working date: <b className="text-t1">{sep.last_working_day ? fmtDMY(sep.last_working_day) : "—"}</b>.
        </div>
        <div className="mb-3">
          <div className={lbl}>New Last Working Date *</div>
          <input type="date" value={date} onChange={e => setDate(e.target.value)}
                 className="w-full min-w-0 px-[10px] py-[7px] bg-bg2 border border-b0 rounded-[10px] text-[14px] text-t0 outline-none" />
        </div>
        {expectedLwd && (
          <div className="mb-3 p-[10px] rounded-lg bg-ab border border-ad flex items-start gap-2">
            <AlertTriangle size={14} color={K.a} className="mt-[2px] flex-shrink-0" />
            <div className="text-[13px] text-t1">Expected last working date as per notice period: <b>{expectedLwd}</b></div>
          </div>
        )}
        <div className="mb-3">
          <div className={lbl}>Reason for change (optional, included in the employee's email)</div>
          <textarea value={reason} onChange={e => setReason(e.target.value)}
                    placeholder="e.g. Early release approved — replacement not required"
                    className="w-full min-w-0 min-h-[60px] p-2 bg-bg2 border border-b0 rounded-lg text-t0 text-[14px] outline-none resize-y" />
        </div>
        <div className="mb-3">
          <div className={lbl}>Replacement required</div>
          <Sl options={[{ v: "No", l: "No" }, { v: "Yes", l: "Yes" }, { v: "Decide later", l: "Decide later" }]}
              value={replacementRequired} onChange={setReplacementRequired} />
        </div>
        {err && <div className="text-[14px] text-r mb-2">{err}</div>}
        <div className="flex gap-2">
          <Bt full onClick={submit} disabled={saving}>{saving ? "Saving…" : "Submit"}</Bt>
          <Bt v="secondary" full onClick={onClose}>Cancel</Bt>
        </div>
      </div>
    </div>
  );
};

// Final HR-approval step — a required note PLUS the chance to confirm/
// adjust the last working date right here, instead of approving first and
// separately using Change Release Date afterward. Shared by the "Manage
// Requests" table and the per-record Overview tab's approval control.
const HrFinalApprovalModal = ({ sep, onClose, onDone }) => {
  const [date, setDate] = useState(sep.last_working_day ? String(sep.last_working_day).slice(0, 10) : "");
  const [remarks, setRemarks] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const submit = async () => {
    setErr("");
    if (!remarks.trim()) return setErr("A note is required before HR can approve this stage.");
    setSaving(true);
    try {
      await api.put(`/api/separation/${sep.id}/approve`, {
        action: "approve",
        remarks: remarks.trim(),
        last_working_day: date || undefined,
      });
      notify("Resignation approved — Notice period begins", { title: "Saved", variant: "success" });
      onDone();
    } catch (e) {
      setErr(e.message || "Failed to approve");
      notify(e.message || "Failed to approve", { title: "Error", variant: "error" });
    } finally { setSaving(false); }
  };

  const lbl = "text-[14px] text-t2 mb-[3px]";
  return (
    <div className="fixed inset-0 animate-fadeIn bg-black/35 z-[200] flex items-center justify-center p-3" onMouseDown={onClose}>
      <div className="animate-modalIn bg-bg1 border border-b1 rounded-2xl p-5 w-full max-w-[480px]" onMouseDown={e => e.stopPropagation()}>
        <div className="flex justify-between items-start mb-4">
          <div className="text-[18px] font-extrabold text-t0">Final HR Approval</div>
          <button onClick={onClose} className="bg-transparent border-0 text-t3 cursor-pointer p-1"><X size={18} /></button>
        </div>
        <div className="mb-3">
          <div className={lbl}>Last Working Date</div>
          <input type="date" value={date} onChange={e => setDate(e.target.value)}
                 className="w-full min-w-0 px-[10px] py-[7px] bg-bg2 border border-b0 rounded-[10px] text-[14px] text-t0 outline-none" />
        </div>
        <div className="mb-3">
          <div className={lbl}>Approval Note (required — saved on the record and emailed to the employee)</div>
          <textarea value={remarks} onChange={e => setRemarks(e.target.value)}
                    placeholder="e.g. Approved. Notice period confirmed, please complete handover with your manager."
                    className="w-full min-w-0 min-h-[70px] p-2 bg-bg2 border border-b0 rounded-lg text-t0 text-[14px] outline-none resize-y" />
        </div>
        {err && <div className="text-[14px] text-r mb-2">{err}</div>}
        <div className="flex gap-2">
          <Bt full onClick={submit} disabled={saving}>{saving ? "Approving…" : "Approve"}</Bt>
          <Bt v="secondary" full onClick={onClose} disabled={saving}>Cancel</Bt>
        </div>
      </div>
    </div>
  );
};

// Final "Mark Completed" step — requires HR/Admin to write a closing note
// (saved on the record, emailed to the employee) and lets them choose
// whether this exit also revokes the employee's HRMS login, instead of
// that deactivation happening silently and unconditionally.
const MarkCompletedModal = ({ sepId, onClose, onSaved }) => {
  const [note, setNote] = useState("");
  const [revokeAccess, setRevokeAccess] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (force = false, forceReason = "") => {
    setErr("");
    if (!note.trim()) return setErr("A closing note is required.");
    setSaving(true);
    try {
      const body = { status: "Completed", completion_note: note.trim(), revoke_access: revokeAccess };
      if (force) { body.force = true; body.force_reason = forceReason; }
      await api.put(`/api/separation/${sepId}/status`, body);
      notify("Separation marked Completed", { title: "Saved", variant: "success" });
      onSaved();
    } catch (e) {
      const msg = e.message || "Failed to mark Completed";
      if (/asset\(s\) still assigned|clearance not yet Completed|FnF is/i.test(msg)) {
        setSaving(false);
        const reason = await promptDialog(msg + "\n\nForce-close anyway? Enter a reason (min 10 chars):", { title: "Force close", multiline: true, variant: "warn", okLabel: "Force close" });
        if (reason && reason.trim().length >= 10) { await submit(true, reason.trim()); return; }
        return;
      }
      setErr(msg);
      notify(msg, { title: "Error", variant: "error" });
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 animate-fadeIn bg-black/35 z-[200] flex items-center justify-center p-3" onMouseDown={onClose}>
      <div className="animate-modalIn bg-bg1 border border-b1 rounded-2xl p-5 w-full max-w-[480px]" onMouseDown={e => e.stopPropagation()}>
        <div className="flex justify-between items-start mb-4">
          <div className="text-[18px] font-extrabold text-t0">Mark Completed</div>
          <button onClick={onClose} className="bg-transparent border-0 text-t3 cursor-pointer p-1"><X size={18} /></button>
        </div>
        <div className="text-[14px] text-t3 mb-3">
          Closes this offboarding record — auto-issues the Experience Letter (monthly batch) and flips the employee to Exited.
        </div>
        <div className="mb-3">
          <div className="text-[14px] text-t2 mb-[3px]">Closing note *</div>
          <textarea value={note} onChange={e => setNote(e.target.value)}
                    placeholder="e.g. All clearances, assets, and F&F settled. Wishing them well for the future."
                    className="w-full min-w-0 min-h-[80px] p-2 bg-bg2 border border-b0 rounded-lg text-t0 text-[14px] outline-none resize-y" />
          <div className="text-[12px] text-t3 mt-1">Kept on the record and emailed to the employee.</div>
        </div>
        <label className="flex items-center gap-2 mb-3 cursor-pointer select-none">
          <input type="checkbox" checked={revokeAccess} onChange={e => setRevokeAccess(e.target.checked)} />
          <span className="text-[14px] text-t1">Revoke this employee's HRMS login access</span>
        </label>
        {err && <div className="text-[14px] text-r mb-2">{err}</div>}
        <div className="flex gap-2">
          <Bt full onClick={() => submit(false)} disabled={saving}>{saving ? "Saving…" : "Mark Completed"}</Bt>
          <Bt v="secondary" full onClick={onClose} disabled={saving}>Cancel</Bt>
        </div>
      </div>
    </div>
  );
};

const fmtD = (d) => { if (!d) return "—"; try { return fmtDMY(d) || d; } catch (e) { return d; } };
const fmtDT = (d) => { if (!d) return "—"; try { return `${fmtDMY(d)}, ${new Date(d).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}`; } catch (e) { return d; } };
const yesNoDash = (v) => v || "—";
// Shared status → color map, one source of truth for the list views below
// and the record detail header, so "Revoked"/"Cancelled"/"Rejected" never
// render as an unqualified green "success" the way a bare isPending/else
// ternary would.
const SEP_STATUS_COLORS = {
  Done: K.g, Completed: K.g, FnF: K.a, "FnF Processing": K.a,
  "In Progress": K.bl, "Exit In Progress": K.bl, "Notice Period": K.pl, Initiated: K.p,
  Pending: K.a, "Pending Manager Approval": K.a, "Pending HR Approval": K.pu,
  Revoked: K.t3, Rejected: K.r, Cancelled: K.t3,
};

// ═══════════════════════════════════════
// MANAGE REQUESTS — Resignation / Termination list views, exact columns
// from the reference (Zoho People Offboarding → Manage Requests).
// ═══════════════════════════════════════
const ManageRequestsView = ({ SP, onOpen, role, onRefresh, can = () => true }) => {
  const [tab, setTab] = useState("resignation");
  const [actBusy, setActBusy] = useState(null);
  const [hrApprovalFor, setHrApprovalFor] = useState(null); // row currently in the Final HR Approval modal
  const rows = SP.filter(s => {
    const t = (s.separation_type || "").toLowerCase();
    return tab === "resignation" ? t === "resignation" : t === "termination";
  });

  const canApprove = (r) => (r.status || "").includes("Pending") && (
    (r.status === "Pending Manager Approval" && (role === "manager" || can('separation', 'manage_requests', null, 'edit'))) ||
    (r.status === "Pending HR Approval" && can('separation', 'manage_requests', null, 'edit') && can('separation', 'lifecycle', null, 'view'))
  );
  const act = async (r, action, e) => {
    e.stopPropagation();
    // Final HR approval collects a required note PLUS the confirmed last
    // working date, via its own modal instead of a single-field prompt.
    if (action === "approve" && r.status === "Pending HR Approval") {
      setHrApprovalFor(r);
      return;
    }
    setActBusy(r.id);
    try {
      await api.put(`/api/separation/${r.id}/approve`, { action });
      notify(`Resignation ${action === "approve" ? "approved" : "rejected"}`, { title: "Saved", variant: "success" });
      onRefresh?.();
    } catch (err) { notify(err.message || `Failed to ${action}`, { title: "Error", variant: "error" }); }
    finally { setActBusy(null); }
  };

  const tabCls = (k) => `px-1 py-3 mr-8 text-[14px] cursor-pointer border-b-2 flex items-center gap-2 ${tab === k ? "font-bold text-p border-p" : "font-semibold text-t2 border-transparent hover:text-t0 hover:border-b1"}`;

  return (
    <Cd style={{ padding: 0, overflow: "hidden" }}>
      <div className="flex gap-0 px-4 pt-3 border-b border-b0">
        <button className={tabCls("resignation")} onClick={() => setTab("resignation")}>Resignation</button>
        <button className={tabCls("termination")} onClick={() => setTab("termination")}>Termination</button>
      </div>
      <div className="overflow-x-auto">
        {tab === "resignation" ? (
          <table className="w-full text-left border-collapse text-[13px]">
            <thead><tr className="bg-bg2 border-b border-b0 divide-x divide-b0 text-[11px] font-bold uppercase tracking-wider text-t3">
              <th className="py-3 pl-6 pr-3">Status</th>
              <th className="py-3 px-3">Employee</th>
              <th className="py-3 px-3">Approver</th>
              <th className="py-3 px-3">Approval Time</th>
              <th className="py-3 px-3">Date of Resignation</th>
              <th className="py-3 px-3">Date of Relieving</th>
              <th className="py-3 px-3">Expected last working date</th>
              <th className="py-3 px-3">Last working date</th>
              <th className="py-3 px-3">Reason of Leaving</th>
              <th className="py-3 px-3">Explanation</th>
              <th className="py-3 px-3">Replacement required</th>
              <th className="py-3 px-3">Attachment</th>
              <th className="py-3 px-3">Notice Period*</th>
              <th className="py-3 pl-3 pr-6 text-right">Actions</th>
            </tr></thead>
            <tbody className="divide-y divide-b0">
              {rows.map(r => {
                const expectedLwd = r.resignation_date && r.notice_period_days != null
                  ? new Date(new Date(r.resignation_date).getTime() + r.notice_period_days * 86400000).toISOString().slice(0, 10)
                  : r.last_working_day;
                const isPending = (r.status || "").includes("Pending");
                return (
                  <tr key={r.id} className="hover:bg-bg2/60 cursor-pointer divide-x divide-b0" onClick={() => onOpen(r)}>
                    <td className="py-3 pl-6 pr-3"><Bg c={SEP_STATUS_COLORS[r.status] || K.bl} dot sm>{r.status}</Bg></td>
                    <td className="py-3 px-3"><div className="flex items-center gap-2"><Av nm={r.employee_name} sz={26} src={r.employee_photo} /><div><div className="font-bold text-t0">{r.employee_name}</div><div className="text-[11px] text-t3 font-mono">{r.employee_id}</div></div></div></td>
                    <td className="py-3 px-3"><div className="flex items-center gap-2"><Av nm={r.manager_name} sz={22} />{r.manager_name || "—"}</div></td>
                    <td className="py-3 px-3 text-t3">{isPending ? "—" : fmtDT(r.updated_at)}</td>
                    <td className="py-3 px-3">{fmtD(r.resignation_date)}</td>
                    <td className="py-3 px-3">{r.status === 'Completed' ? fmtD(r.last_working_day) : "—"}</td>
                    <td className="py-3 px-3">{fmtD(expectedLwd)}</td>
                    <td className="py-3 px-3 font-semibold text-t0">{fmtD(r.last_working_day)}</td>
                    <td className="py-3 px-3">{r.reason_category || "—"}</td>
                    <td className="py-3 px-3 max-w-[220px] truncate text-t2">{r.reason_detail || "—"}</td>
                    <td className="py-3 px-3">{r.replacement_required || "Decide later"}</td>
                    <td className="py-3 px-3">{r.attachment_name ? <span className="text-bl underline">{r.attachment_name}</span> : "—"}</td>
                    <td className="py-3 px-3">{r.notice_period_days != null ? r.notice_period_days + "d" : "—"}</td>
                    <td className="py-3 pl-3 pr-6 text-right">
                      {canApprove(r) ? (
                        <div className="flex items-center justify-end gap-[6px]">
                          <Bt v="success" s="xs" icon={Check} disabled={actBusy === r.id} onClick={(e) => act(r, "approve", e)} />
                          <Bt v="danger" s="xs" icon={X} disabled={actBusy === r.id} onClick={(e) => act(r, "reject", e)} />
                        </div>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <table className="w-full text-left border-collapse text-[13px]">
            <thead><tr className="bg-bg2 border-b border-b0 divide-x divide-b0 text-[11px] font-bold uppercase tracking-wider text-t3">
              <th className="py-3 pl-6 pr-3">Employee</th>
              <th className="py-3 px-3">Date of Termination</th>
              <th className="py-3 px-3">Reason</th>
              <th className="py-3 px-3">Explanation</th>
              <th className="py-3 px-3">Last working date</th>
              <th className="py-3 px-3">Login disable date</th>
              <th className="py-3 px-3">Replacement required</th>
              <th className="py-3 px-3">Attachment</th>
              <th className="py-3 px-3">FNF Eligibility</th>
              <th className="py-3 pl-3 pr-6">Termination Type:*</th>
            </tr></thead>
            <tbody className="divide-y divide-b0">
              {rows.map(r => (
                <tr key={r.id} className="hover:bg-bg2/60 cursor-pointer divide-x divide-b0" onClick={() => onOpen(r)}>
                  <td className="py-3 pl-6 pr-3"><div className="flex items-center gap-2"><Av nm={r.employee_name} sz={26} src={r.employee_photo} /><div><div className="font-bold text-t0">{r.employee_name}</div><div className="text-[11px] text-t3 font-mono">{r.employee_id}</div></div></div></td>
                  <td className="py-3 px-3">{fmtD(r.resignation_date)}</td>
                  <td className="py-3 px-3">{r.reason_category || "—"}</td>
                  <td className="py-3 px-3 max-w-[220px] truncate text-t2">{r.reason_detail || "—"}</td>
                  <td className="py-3 px-3 font-semibold text-t0">{fmtD(r.last_working_day)}</td>
                  <td className="py-3 px-3">{fmtD(r.login_disable_date)}</td>
                  <td className="py-3 px-3">{r.replacement_required || "Decide later"}</td>
                  <td className="py-3 px-3">{r.attachment_name ? <span className="text-bl underline">{r.attachment_name}</span> : "—"}</td>
                  <td className="py-3 px-3">{yesNoDash(r.fnf_eligibility)}</td>
                  <td className="py-3 pl-3 pr-6">{r.termination_type || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {rows.length === 0 && <div className="text-center p-[30px] text-t3 text-[14px]">No {tab === "resignation" ? "resignations" : "terminations"} recorded</div>}
      </div>
      {hrApprovalFor && (
        <HrFinalApprovalModal
          sep={hrApprovalFor}
          onClose={() => setHrApprovalFor(null)}
          onDone={() => { setHrApprovalFor(null); onRefresh?.(); }}
        />
      )}
    </Cd>
  );
};

// ═══════════════════════════════════════
// TRACK OFFBOARDING — status-bucketed view + "Add Termination".
// ═══════════════════════════════════════
const TRACK_BUCKETS = [
  { k: "inprogress", l: "In progress", match: s => !["Completed", "Rejected", "Cancelled", "Revoked"].includes(s.status) },
  { k: "completed", l: "Completed", match: s => s.status === "Completed" },
  { k: "rejected", l: "Rejected", match: s => s.status === "Rejected" },
  { k: "cancelled", l: "Cancelled", match: s => s.status === "Cancelled" },
  { k: "revoked", l: "Revoked", match: s => s.status === "Revoked" },
  { k: "all", l: "All", match: () => true },
];

const TrackOffboardingView = ({ role, SP, employees, options, onOpen, onRefresh, can = () => true }) => {
  const [bucket, setBucket] = useState("inprogress");
  const [showAdd, setShowAdd] = useState(false);
  const [cancelBusy, setCancelBusy] = useState(null);
  // Right-side Filter panel — Employee / Exit type / Last working date
  // range / Department, matching the reference. Draft values live in the
  // panel; Apply commits them to `filters` (what actually narrows `rows`
  // below), Reset clears both — the panel doesn't filter on every
  // keystroke.
  const [showFilter, setShowFilter] = useState(false);
  const emptyFilters = { employeeId: "", exitType: "", lwdFrom: "", lwdTo: "", department: "" };
  const [draftFilters, setDraftFilters] = useState(emptyFilters);
  const [filters, setFilters] = useState(emptyFilters);
  const setDraft = (k, v) => setDraftFilters(p => ({ ...p, [k]: v }));
  const applyFilters = () => { setFilters(draftFilters); setShowFilter(false); };
  const resetFilters = () => { setDraftFilters(emptyFilters); setFilters(emptyFilters); };
  const filterCount = Object.values(filters).filter(Boolean).length;
  const departments = Array.from(new Set(employees.map(e => e.dept).filter(Boolean))).sort();

  const activeBucket = TRACK_BUCKETS.find(b => b.k === bucket) || TRACK_BUCKETS[0];
  const rows = SP.filter(activeBucket.match).filter(r => {
    if (filters.employeeId && r.employee_id !== filters.employeeId) return false;
    if (filters.exitType && r.separation_type !== filters.exitType) return false;
    if (filters.department) {
      const emp = employees.find(e => e.id === r.employee_id);
      if (!emp || emp.dept !== filters.department) return false;
    }
    const lwd = r.last_working_day ? String(r.last_working_day).slice(0, 10) : null;
    if (filters.lwdFrom && (!lwd || lwd < filters.lwdFrom)) return false;
    if (filters.lwdTo && (!lwd || lwd > filters.lwdTo)) return false;
    return true;
  });

  const doCancel = async (r) => {
    if (!(await confirmDialog(`Cancel separation ${r.id} for ${r.employee_name}?`))) return;
    setCancelBusy(r.id);
    try {
      await api.put(`/api/separation/${r.id}/cancel`, {});
      notify("Separation cancelled", { title: "Saved", variant: "success" });
      onRefresh?.();
    } catch (e) { notify(e.message || "Failed to cancel", { title: "Error", variant: "error" }); }
    finally { setCancelBusy(null); }
  };

  return (
    <div className="flex flex-col lg:flex-row gap-4">
      <div className="lg:w-[200px] flex-shrink-0">
        <Cd style={{ padding: 8 }}>
          {TRACK_BUCKETS.map(b => (
            <button key={b.k} onClick={() => setBucket(b.k)}
              className={`w-full text-left px-3 py-[9px] rounded-lg text-[14px] font-semibold cursor-pointer border-0 mb-[2px] flex items-center justify-between ${bucket === b.k ? "bg-pg text-p" : "bg-transparent text-t2 hover:bg-bg2"}`}>
              {b.l}
              <span className="text-[11px] font-bold text-t3">{SP.filter(b.match).length}</span>
            </button>
          ))}
        </Cd>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex justify-between items-center mb-3">
          <div className="text-[16px] font-bold text-t0">{activeBucket.l} ({rows.length})</div>
          <div className="flex gap-2">
            <Bt v={showFilter ? "primary" : "secondary"} s="sm" icon={Filter} onClick={() => setShowFilter(v => !v)}>Filter{filterCount > 0 ? ` (${filterCount})` : ""}</Bt>
            {can('separation', 'track', 'add_termination', 'edit') && <Bt icon={Plus} s="sm" onClick={() => setShowAdd(true)}>Add Request</Bt>}
          </div>
        </div>
        <Cd style={{ padding: 0, overflow: "hidden" }}>
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse text-[13px]">
              <thead><tr className="bg-bg2 border-b border-b0 text-[11px] font-bold uppercase tracking-wider text-t3">
                <th className="py-3 pl-6 pr-3">Employee</th>
                <th className="py-3 px-3">Type</th>
                <th className="py-3 px-3">Status</th>
                <th className="py-3 px-3">Last working date</th>
                <th className="py-3 pl-3 pr-6 text-right">Actions</th>
              </tr></thead>
              <tbody className="divide-y divide-b0">
                {rows.map(r => (
                  <tr key={r.id} className="hover:bg-bg2/60">
                    <td className="py-3 pl-6 pr-3 cursor-pointer" onClick={() => onOpen(r)}>
                      <div className="flex items-center gap-2"><Av nm={r.employee_name} sz={28} src={r.employee_photo} /><div><div className="font-bold text-t0">{r.employee_name}</div><div className="text-[11px] text-t3 font-mono">{r.employee_id}</div></div></div>
                    </td>
                    <td className="py-3 px-3">{r.separation_type}</td>
                    <td className="py-3 px-3"><Bg c={SEP_STATUS_COLORS[r.status] || K.bl} sm>{r.status}</Bg></td>
                    <td className="py-3 px-3">{fmtD(r.last_working_day)}</td>
                    <td className="py-3 pl-3 pr-6 text-right">
                      <div className="flex items-center justify-end gap-[6px]">
                        <Bt v="secondary" s="xs" icon={Eye} onClick={() => onOpen(r)}>View</Bt>
                        {can('separation', 'track', null, 'edit') && !["Completed", "Rejected", "Cancelled", "Revoked"].includes(r.status) && (
                          <Bt v="danger" s="xs" icon={Ban} disabled={cancelBusy === r.id} onClick={() => doCancel(r)}>{cancelBusy === r.id ? "…" : "Cancel"}</Bt>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {rows.length === 0 && <div className="text-center p-[30px] text-t3 text-[14px]">Nothing here</div>}
          </div>
        </Cd>
      </div>
      {showFilter && (
        <div className="lg:w-[260px] flex-shrink-0">
          <Cd style={{ padding: 14 }}>
            <div className="flex items-center justify-between mb-3">
              <div className="text-[14px] font-bold text-t0">Filter</div>
              <button onClick={() => setShowFilter(false)} className="bg-transparent border-0 text-t3 cursor-pointer p-1"><X size={16} /></button>
            </div>
            <div className="mb-3">
              <div className="text-[13px] text-t2 mb-1">Employee</div>
              <SearchSelect
                placeholder="All Employees"
                searchPlaceholder="Search by name or ID…"
                options={employees.map(e => ({ v: e.id, l: e.nm || e.name || e.employee_name || e.id, sub: [e.id, e.dept].filter(Boolean).join(' · ') }))}
                value={draftFilters.employeeId}
                onChange={v => setDraft('employeeId', v)} />
            </div>
            <div className="mb-3">
              <div className="text-[13px] text-t2 mb-1">Exit Type</div>
              <Sl options={[{ v: "", l: "All" }, ...(options.separation_types || []).map(t => ({ v: t, l: t }))]}
                  value={draftFilters.exitType} onChange={v => setDraft('exitType', v)} />
            </div>
            <div className="mb-3">
              <div className="text-[13px] text-t2 mb-1">Last working date</div>
              <div className="grid grid-cols-2 gap-2">
                <input type="date" value={draftFilters.lwdFrom} onChange={e => setDraft('lwdFrom', e.target.value)}
                       className="w-full min-w-0 px-[8px] py-[6px] bg-bg2 border border-b0 rounded-[8px] text-[13px] text-t0 outline-none" />
                <input type="date" value={draftFilters.lwdTo} onChange={e => setDraft('lwdTo', e.target.value)}
                       className="w-full min-w-0 px-[8px] py-[6px] bg-bg2 border border-b0 rounded-[8px] text-[13px] text-t0 outline-none" />
              </div>
            </div>
            <div className="mb-4">
              <div className="text-[13px] text-t2 mb-1">Department</div>
              <Sl options={[{ v: "", l: "All Departments" }, ...departments.map(d => ({ v: d, l: d }))]}
                  value={draftFilters.department} onChange={v => setDraft('department', v)} />
            </div>
            <div className="flex gap-2">
              <Bt s="sm" full onClick={applyFilters}>Apply</Bt>
              <Bt v="secondary" s="sm" full onClick={resetFilters}>Reset</Bt>
            </div>
          </Cd>
        </div>
      )}
      {showAdd && <AddTerminationModal
        employees={employees}
        options={options}
        onClose={() => setShowAdd(false)}
        onSubmit={async (v, files) => { const created = await api.post("/api/separation", v); await uploadSepFiles(created?.separation?.id, files); onRefresh?.(); }} />}
    </div>
  );
};

// ═══════════════════════════════════════
// CLEARANCES — IT / HR / Admin, each its own structured sign-off form.
// ═══════════════════════════════════════
const CLEARANCE_TYPES = [
  { k: "it", l: "IT clearance", ic: Monitor },
  { k: "hr", l: "HR clearance", ic: Users },
  { k: "admin", l: "Admin clearance", ic: Building2 },
];

const ClearanceDetailModal = ({ type, row, onClose, onSaved, canEdit }) => {
  const [vals, setVals] = useState(row);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setVals(p => ({ ...p, [k]: v }));
  // View-only viewers (centre_hr) get plain text, not live controls — the
  // backend already 403s their PUT, but a disabled-looking input still
  // invites a click; a flat read-out doesn't.
  const yn = (k, label) => (
    <div className="flex items-center justify-between py-[7px] border-b border-b0">
      <span className="text-[14px] text-t2">{label}</span>
      {canEdit
        ? <Sl options={[{ v: "", l: "—" }, { v: "Yes", l: "Yes" }, { v: "No", l: "No" }]} value={vals[k] || ""} onChange={v => set(k, v)} />
        : <span className="text-[14px] font-semibold text-t0">{vals[k] || "—"}</span>}
    </div>
  );
  const text = (k, label) => (
    <div className="py-[7px] border-b border-b0">
      <div className="text-[14px] text-t2 mb-1">{label}</div>
      {canEdit ? <Inp value={vals[k] || ""} onChange={v => set(k, v)} /> : <div className="text-[14px] text-t0">{vals[k] || "—"}</div>}
    </div>
  );

  const save = async (complete) => {
    setSaving(true);
    try {
      const body = { ...vals, complete: !!complete };
      delete body.id; delete body.separation_id; delete body.employee_id; delete body.employee_name;
      delete body.designation; delete body.last_working_day; delete body.offboarding_id;
      delete body.created_at; delete body.updated_at; delete body.status; delete body.completed_on;
      const r = await api.put(`/api/separation/clearances/${type}/${row.id}`, body);
      notify(complete ? "Clearance completed" : "Saved", { title: "Saved", variant: "success" });
      onSaved(r);
    } catch (e) { notify(e.message || "Failed to save", { title: "Error", variant: "error" }); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 animate-fadeIn bg-black/40 z-[220] flex items-center justify-center p-3" onMouseDown={onClose}>
      <div className="animate-modalIn bg-bg1 border border-b1 rounded-2xl w-full max-w-[640px] max-h-[90vh] overflow-auto" onMouseDown={e => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-b0 sticky top-0 bg-bg1 flex items-center justify-between">
          <div>
            <div className="text-[16px] font-extrabold text-t0">{row.employee_name} ({row.employee_id})</div>
            <div className="text-[14px] text-t3 mt-[2px]">Last working date {fmtD(row.last_working_day)} · Status <Bg c={vals.status === 'Completed' ? K.g : K.a} sm>{vals.status}</Bg></div>
          </div>
          <button onClick={onClose} className="bg-transparent border-0 text-t3 cursor-pointer p-1"><X size={18} /></button>
        </div>
        <div className="p-5">
          {type === "it" && <>
            <div className="text-[13px] font-bold text-t3 uppercase tracking-wide mb-1">Company Property Return</div>
            {yn("laptop_computer_returned", "Laptop/Computer Returned?")}
            {yn("mobile_phone_returned", "Mobile Phone Returned?")}
            {yn("keys_access_cards_returned", "Keys/Access Cards Returned?")}
            {text("other_equipment_returned", "Other Equipment Returned? (Specify)")}
            <div className="text-[13px] font-bold text-t3 uppercase tracking-wide mb-1 mt-4">Access Revocation</div>
            {yn("email_account_deactivated", "Email Account Deactivated?")}
            {yn("system_access_revoked", "System Access Revoked?")}
            {yn("vpn_access_revoked", "VPN Access Revoked?")}
            {yn("software_licenses_deactivated", "Software Licenses Deactivated?")}
          </>}
          {type === "hr" && <>
            {yn("update_reporting_manager_team", "Update Reporting Manager his/her team")}
            <div className="text-[13px] font-bold text-t3 uppercase tracking-wide mb-1 mt-4">Exit Interview</div>
            {yn("exit_interview_completed", "Has the exit interview been completed?")}
            <div className="py-[7px] border-b border-b0"><div className="text-[14px] text-t2 mb-1">Exit interview date</div>
              {canEdit
                ? <input type="date" value={vals.exit_interview_date ? String(vals.exit_interview_date).slice(0, 10) : ""} onChange={e => set('exit_interview_date', e.target.value)}
                         className="w-full min-w-0 px-[10px] py-[7px] bg-bg2 border border-b0 rounded-[10px] text-[14px] text-t0 outline-none" />
                : <div className="text-[14px] text-t0">{fmtD(vals.exit_interview_date)}</div>}
            </div>
            {text("exit_interview_conducted_by", "Exit interview conducted by:")}
            <div className="text-[13px] font-bold text-t3 uppercase tracking-wide mb-1 mt-4">Final Payments and Benefits</div>
            {yn("benefits_terminated_or_transitioned", "Have all other benefits been terminated or transitioned?")}
            {yn("final_paycheck_processed", "Has the final paycheck been processed?")}
            <div className="text-[13px] font-bold text-t3 uppercase tracking-wide mb-1 mt-4">Documentation</div>
            {yn("forms_documents_signed", "Have all necessary forms and documents been signed?")}
            {yn("nda_signed", "Has the non-disclosure agreement (NDA) been signed?")}
            {yn("confidentiality_agreements_signed", "Have any required confidentiality agreements been signed?")}
            <div className="text-[13px] font-bold text-t3 uppercase tracking-wide mb-1 mt-4">Comments/Notes</div>
            {canEdit
              ? <textarea value={vals.comments_notes || ""} onChange={e => set('comments_notes', e.target.value)}
                          placeholder="Additional comments or notes"
                          className="w-full min-w-0 min-h-[60px] p-2 bg-bg2 border border-b0 rounded-lg text-t0 text-[14px] outline-none resize-y" />
              : <div className="text-[14px] text-t0 whitespace-pre-wrap">{vals.comments_notes || "—"}</div>}
          </>}
          {type === "admin" && <>
            {yn("id_badge_returned", "ID Badge Returned")}
            {yn("building_access_revoked", "Building Access Revoked?")}
            {yn("access_cards_keys_returned", "Access Cards/Keys Returned")}
            {yn("parking_permit_returned", "Parking Permit Returned")}
            {text("other_company_property_returned", "Other Company Property Returned (Specify)")}
          </>}
        </div>
        <div className="px-5 py-4 border-t border-b0 flex gap-2 sticky bottom-0 bg-bg1">
          {canEdit && <Bt disabled={saving} onClick={() => save(true)}>{saving ? "Saving…" : "Submit and Complete"}</Bt>}
          {canEdit && <Bt v="secondary" disabled={saving} onClick={() => save(false)}>Save</Bt>}
          <Bt v="ghost" onClick={onClose}>Close</Bt>
        </div>
      </div>
    </div>
  );
};

const ClearancesView = ({ role, can = () => true }) => {
  const [tab, setTab] = useState("it");
  const [rows, setRows] = useState(null);
  const [openRow, setOpenRow] = useState(null);

  const load = () => {
    setRows(null);
    api.get(`/api/separation/clearances/${tab}`).then(setRows).catch(() => setRows([]));
  };
  useEffect(load, [tab]); // eslint-disable-line

  const cols = CLEARANCE_FIELDS_DISPLAY[tab];
  const tabCls = (k) => `px-1 py-3 mr-8 text-[14px] cursor-pointer border-b-2 flex items-center gap-2 ${tab === k ? "font-bold text-p border-p" : "font-semibold text-t2 border-transparent hover:text-t0 hover:border-b1"}`;

  return (
    <Cd style={{ padding: 0, overflow: "hidden" }}>
      <div className="flex gap-0 px-4 pt-3 border-b border-b0 flex-wrap">
        {CLEARANCE_TYPES.map(t => <button key={t.k} className={tabCls(t.k)} onClick={() => setTab(t.k)}><t.ic size={14} className="text-t3" />{t.l}</button>)}
      </div>
      <div className="overflow-x-auto">
        {rows === null ? <div className="text-center p-[30px] text-t3 text-[14px]">Loading…</div> : (
          <table className="w-full text-left border-collapse text-[13px]">
            <thead><tr className="bg-bg2 border-b border-b0 text-[11px] font-bold uppercase tracking-wider text-t3">
              <th className="py-3 pl-6 pr-3">Clearance Status</th>
              <th className="py-3 px-3">Clearance Time</th>
              <th className="py-3 px-3">Employee</th>
              <th className="py-3 px-3">Offboarding ID</th>
              {cols.map(c => <th key={c.k} className="py-3 px-3">{c.l}</th>)}
            </tr></thead>
            <tbody className="divide-y divide-b0">
              {rows.map(r => (
                <tr key={r.id} className="hover:bg-bg2/60 cursor-pointer" onClick={() => setOpenRow(r)}>
                  <td className="py-3 pl-6 pr-3"><Bg c={r.status === 'Completed' ? K.g : K.a} dot sm>{r.status}</Bg></td>
                  <td className="py-3 px-3">{fmtDT(r.completed_on)}</td>
                  <td className="py-3 px-3"><div className="flex items-center gap-2"><Av nm={r.employee_name} sz={26} src={r.employee_photo} /><div><div className="font-bold text-t0">{r.employee_name}</div><div className="text-[11px] text-t3 font-mono">{r.employee_id}</div></div></div></td>
                  <td className="py-3 px-3">{r.separation_id}</td>
                  {cols.map(c => <td key={c.k} className="py-3 px-3">{yesNoDash(r[c.k])}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {rows && rows.length === 0 && <div className="text-center p-[30px] text-t3 text-[14px]">No {tab.toUpperCase()} clearance records</div>}
      </div>
      {openRow && <ClearanceDetailModal type={tab} row={openRow} canEdit={can('separation', 'clearances', null, 'edit')} onClose={() => setOpenRow(null)} onSaved={() => { setOpenRow(null); load(); }} />}
    </Cd>
  );
};
const CLEARANCE_FIELDS_DISPLAY = {
  it: [
    { k: "laptop_computer_returned", l: "Laptop/Computer Returned?" },
    { k: "mobile_phone_returned", l: "Mobile Phone Returned?" },
    { k: "keys_access_cards_returned", l: "Keys/Access Cards Returned?" },
    { k: "other_equipment_returned", l: "Other Equipment Returned? (Specify)" },
    { k: "email_account_deactivated", l: "Email Account Deactivated?" },
    { k: "system_access_revoked", l: "System Access Revoked?" },
    { k: "vpn_access_revoked", l: "VPN Access Revoked?" },
    { k: "software_licenses_deactivated", l: "Software Licenses Deactivated?" },
  ],
  hr: [
    { k: "exit_interview_completed", l: "Exit Interview Completed?" },
    { k: "final_paycheck_processed", l: "Final Paycheck Processed?" },
    { k: "nda_signed", l: "NDA Signed?" },
  ],
  admin: [
    { k: "id_badge_returned", l: "ID Badge Returned" },
    { k: "building_access_revoked", l: "Building Access Revoked?" },
    { k: "access_cards_keys_returned", l: "Access Cards/Keys Returned" },
    { k: "parking_permit_returned", l: "Parking Permit Returned" },
  ],
};

export const SeparationPage = ({ spData = [], employees = [], role, onRefresh, hashQuery, myEmployeeId, can = () => true }) => {
  const [selSep, setSelSep] = useState(null);
  const [sepDetail, setSepDetail] = useState(null);
  // Template-vs-current diff for the open separation. Drives the "sync"
  // banner on the Exit Checklist + Assets tabs when the templates have
  // been edited after this separation was created.
  const [tplDiff, setTplDiff] = useState(null);
  const reloadTplDiff = (sepId) => {
    if (!sepId) return setTplDiff(null);
    api.get("/api/separation/" + sepId + "/template-diff")
       .then(d => setTplDiff(d))
       .catch(() => setTplDiff(null));
  };
  const [sepTab, setSepTab] = useState("overview");
  const [openClrType, setOpenClrType] = useState(null); // 'it' | 'hr' | 'admin' | null — this record's Clearances tab
  const [showReleaseDateModal, setShowReleaseDateModal] = useState(false);
  const [showMarkCompletedModal, setShowMarkCompletedModal] = useState(false);
  const [showHrApprovalModal, setShowHrApprovalModal] = useState(false);
  const [approveBusy, setApproveBusy] = useState(false);
  const [reportActBusy, setReportActBusy] = useState(null); // approve/reject busy flag, plain-employee "Pending Your Approval" list
  const [sepAssetForm, setSepAssetForm] = useState(false);
  const [showForm, setShowForm] = useState(null);
  // Config-driven options replace what used to be hardcoded enums in this
  // file. Pulled from /api/separation/config/options at mount.
  const [sepOptions, setSepOptions] = useState({
    separation_types: [], reason_categories: [],
    notice_period_default: 30, days_after_lwd: 45,
  });
  // When the user picks an employee in Initiate Exit, look up that
  // employee's contractual notice period and pre-fill the form. Tracked
  // here so the FormModal can read the live notice value via initialValues.
  const [initiateEmpId, setInitiateEmpId] = useState("");
  const [employeeNoticeDays, setEmployeeNoticeDays] = useState(null);
  // Per-action busy flags for feedback-layer loading states (Saving.../disabled)
  // on the various lifecycle / doc / sync / FnF controls below.
  const [advBusy, setAdvBusy] = useState(false);
  const [revokeBusy, setRevokeBusy] = useState(false);
  const [regenBusy, setRegenBusy] = useState(false);
  const [sendLetterBusy, setSendLetterBusy] = useState(null);
  const [supDocBusy, setSupDocBusy] = useState(false);
  const supDocRef = useRef(null);
  const [decisionBusy, setDecisionBusy] = useState(false);
  const [syncBusy, setSyncBusy] = useState(false);
  const [bulkReturnBusy, setBulkReturnBusy] = useState(false);
  const [assetActBusy, setAssetActBusy] = useState(null);
  const [fnfCalcBusy, setFnfCalcBusy] = useState(false);
  const [fnfApproveBusy, setFnfApproveBusy] = useState(false);
  const [fnfSaveBusy, setFnfSaveBusy] = useState(false);
  // null = read-only view; an object = manual-edit draft of the 5 FnF amounts
  const [fnfEditVals, setFnfEditVals] = useState(null);
  // Top-level Offboarding nav — Manage Requests / Track Offboarding /
  // Clearances, mirroring the reference's three sections.
  const [mgmtTab, setMgmtTab] = useState("requests");

  useEffect(() => {
    api.get("/api/separation/config/options").then(d => setSepOptions(prev => ({ ...prev, ...d }))).catch(() => {});
  }, []);

  useEffect(() => {
    if (!initiateEmpId) { setEmployeeNoticeDays(null); return; }
    api.get("/api/separation/config/options?employee_id=" + encodeURIComponent(initiateEmpId))
      .then(d => setEmployeeNoticeDays(d.effective_notice_period ?? null))
      .catch(() => setEmployeeNoticeDays(null));
  }, [initiateEmpId]);

  useEffect(() => {
    if (selSep) {
      setSepTab("overview");
      setSepDetail(null);
      setTplDiff(null);
      setFnfEditVals(null);
      setShowMarkCompletedModal(false);
      api.get("/api/separation/" + selSep.id).then(d => setSepDetail(d)).catch(() => setSepDetail(selSep));
      reloadTplDiff(selSep.id);
    }
  }, [selSep]);

  const SP = spData;

  // Opened via a #separation?id=<sepId> deep link — a notification (new
  // resignation awaiting approval, approval-chain update, HR-initiated
  // exit, ...) pointing at a specific record. Consumed once per id so a
  // manual "Back to Separations" click doesn't immediately reopen it.
  const consumedHashId = useRef(null);
  useEffect(() => {
    const id = hashQuery?.id;
    if (!id || consumedHashId.current === id) return;
    const match = SP.find(s => s.id === id);
    if (match) { consumedHashId.current = id; setSelSep(match); }
  }, [hashQuery?.id, SP]);

  const sepStages = ["Initiated", "Notice Period", "Exit In Progress", "FnF Processing", "Completed"];
  const sepTypeColors = { Resignation: K.a, Termination: K.r, Retirement: K.bl, "Mutual Separation": K.pu, "End of Contract": K.tl, Absconding: K.r, Resign: K.a, Terminated: K.r };
  const sepStatusColors = SEP_STATUS_COLORS;
  const fnfStatusColors = { Settled: K.g, Approved: K.g, Pending: K.a, "In Progress": K.bl, "Not Started": K.t3 };
  const assetStatusColors = { "Pending Return": K.a, Returned: K.g, Damaged: K.r, Lost: K.r, Waived: K.t3 };

  const fmtCur = (v) => { if (!v && v !== 0) return "--"; const n = Number(v); return "₹" + n.toLocaleString("en-IN"); };
  const fmtDate = (d) => { if (!d) return "--"; try { return fmtDMY(d) || d; } catch (e) { return d; } };
  // Whole calendar days, both ends truncated to midnight — not a raw
  // millisecond diff off the current time-of-day (that used Math.ceil
  // against "right now", which could round a partial day UP past the
  // employee's actual total notice period, e.g. showing "9d left" on an
  // "8d" notice period).
  const noticeDaysLeft = (lastDay) => {
    if (!lastDay) return "--";
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const end = new Date(lastDay); end.setHours(0, 0, 0, 0);
    const diff = Math.round((end - today) / (1000 * 60 * 60 * 24));
    return diff > 0 ? diff + "d" : "Done";
  };

  if (selSep) {
    const raw = sepDetail || {};
    const sd = raw.separation || raw || selSep;
    const checklist = raw.checklist || sd.checklist || [];
    const assets = raw.assets || sd.assets || [];
    // Live tracked assignments from the core asset module — separate from
    // the free-form `assets` rows above.
    const trackedAssets = raw.tracked_assets || [];
    const trackedOpen = trackedAssets.filter(a => a.state !== 'returned').length;
    const fnfObj ={ basic_dues: sd.fnf_basic_dues, leave_encashment: sd.fnf_leave_encashment, bonus: sd.fnf_bonus, gratuity: sd.fnf_gratuity, deductions: sd.fnf_deductions, net_payable: sd.fnf_net_payable, status: sd.fnf_status, approved_by: sd.fnf_approved_by, paid_date: sd.fnf_paid_date, due_date: sd.fnf_due_date };

    const clGroups = Array.isArray(checklist) ? {} : checklist;
    if (Array.isArray(checklist)) { checklist.forEach(item => { const cat = item.category || "General"; if (!clGroups[cat]) clGroups[cat] = []; clGroups[cat].push(item); }); }
    const allClItems = Array.isArray(checklist) ? checklist : Object.values(checklist).flat();
    const totalCl = allClItems.length || 1;
    const doneCl = allClItems.filter(c => c.is_completed || c.completed || c.done).length;
    const clPct = Math.round(doneCl / totalCl * 100);
    const assetsReturned = assets.filter(a => a.status === "Returned").length;
    const currentStage = sd.status === "Done" || sd.status === "Completed" ? 4 : sd.status === "FnF" || sd.status === "FnF Processing" ? 3 : (sd.status || "").includes("Exit") || (sd.status || "").includes("Progress") ? 2 : (sd.status || "").includes("Notice") ? 1 : 0;
    const earnings = Array.isArray(fnfObj.earnings) ? fnfObj.earnings : [{ label: "Basic Dues", amount: Number(fnfObj.basic_dues) || 0 }, { label: "Leave Encashment", amount: Number(fnfObj.leave_encashment) || 0 }, { label: "Bonus", amount: Number(fnfObj.bonus) || 0 }, { label: "Gratuity", amount: Number(fnfObj.gratuity) || 0 }].filter(e => e.amount > 0);
    const deductionsAmt = Number(fnfObj.deductions) || 0;
    const deductions = Array.isArray(fnfObj.deductions_list) ? fnfObj.deductions_list : [{ label: "Deductions (Notice/Loans)", amount: deductionsAmt }].filter(e => e.amount > 0);
    const totalEarnings = earnings.reduce((s, e) => s + (e.amount || 0), 0);
    const totalDeductions = deductions.reduce((s, e) => s + (e.amount || 0), 0);
    const netPayable = Number(fnfObj.net_payable) || totalEarnings - totalDeductions;

    // Guided workflow: once a tab's own requirement is fully met, move the
    // lifecycle status forward and jump to the next tab automatically —
    // instead of HR bouncing back to Overview each time to click "next
    // stage". Only fires from the exact expected `fromStatus` (never
    // backwards, never re-fires a no-op transition) and only for roles
    // that can actually call PUT /:id/status; other roles still get the
    // tab switch, just not the status change (they'd get a 403 anyway).
    const autoAdvanceStatus = async (curStatus, fromStatus, toStatus) => {
      if (curStatus !== fromStatus || !can('separation', 'lifecycle', null, 'view')) return false;
      try {
        await api.put(`/api/separation/${selSep.id}/status`, { status: toStatus });
        notify(`All done — moved to ${toStatus}`, { title: 'Advanced', variant: 'success' });
        return true;
      } catch (e) { return false; }
    };
    const afterClearanceSave = async () => {
      setOpenClrType(null);
      const d = await api.get('/api/separation/' + selSep.id).catch(() => null);
      if (!d) return;
      const cl = d.clearances || {};
      const allDone = ['it', 'hr', 'admin'].every(k => cl[k]?.status === 'Completed');
      if (allDone) {
        setSepTab('assets');
        const curStatus = (d.separation || d).status;
        const moved = await autoAdvanceStatus(curStatus, 'Notice Period', 'Exit In Progress');
        if (moved) return setSepDetail(await api.get('/api/separation/' + selSep.id).catch(() => d));
      }
      setSepDetail(d);
    };
    const toggleChecklistItem = async (item) => {
      try {
        if (item.is_completed) {
          await api.put(`/api/separation/${selSep.id}/checklist/${item.id}/undo`, {});
        } else {
          await api.put(`/api/separation/${selSep.id}/checklist/${item.id}`, {});
        }
        api.get('/api/separation/' + selSep.id).then(d => setSepDetail(d));
      } catch (e) {
        notify(e.message || 'Failed to update checklist item', { title: 'Error', variant: 'error' });
      }
    };
    const afterAssetChange = async () => {
      const d = await api.get('/api/separation/' + selSep.id).catch(() => null);
      if (!d) return;
      const ta = d.tracked_assets || [];
      const la = d.assets || [];
      const allDone = ta.every(a => a.state === 'returned') && la.every(a => ['Returned', 'Damaged', 'Lost', 'Waived'].includes(a.status));
      if (allDone) {
        const curStatus = (d.separation || d).status;
        const moved = await autoAdvanceStatus(curStatus, 'Exit In Progress', 'FnF Processing');
        if (moved) {
          setSepTab('fnf');
          return setSepDetail(await api.get('/api/separation/' + selSep.id).catch(() => d));
        }
      }
      setSepDetail(d);
    };

    // Forward-status control, hoisted (not just inside the Overview tab's
    // Lifecycle Controls card) so Clearances/Assets/Exit Interview/FnF can
    // each offer their own "Continue" action — HR no longer has to bounce
    // back to Overview to move the record forward. `afterTab` lets a
    // caller land on a specific tab once the status change lands (e.g.
    // Assets → Exit Interview), independent of the backend stage name.
    const cur = sd.status;
    const next = cur === 'Notice Period' ? { to: 'Exit In Progress', label: 'Start Exit Formalities', note: 'Auto-issues the Relieving Letter.' }
               : cur === 'Exit In Progress' ? { to: 'FnF Processing', label: 'Move to F&F Processing', note: 'Signals payroll to compute the settlement.' }
               : cur === 'FnF Processing' ? { to: 'Completed', label: 'Close & Mark Completed', note: 'Auto-issues Experience Letter, flips employee to Exited. All clearances + F&F must be Approved. You\'ll write a closing note and choose whether to revoke login access.' }
               : null;
    const doAdvance = async (target, force = false, afterTab) => {
      setAdvBusy(true);
      try {
        let body = { status: target };
        if (force) {
          const reason = await promptDialog(`Force-close with open items? Enter a reason (min 10 chars):`, { title: 'Force close', multiline: true, variant: 'warn', okLabel: 'Force close' });
          if (!reason || reason.trim().length < 10) return;
          body = { ...body, force: true, force_reason: reason.trim() };
        }
        await api.put(`/api/separation/${selSep.id}/status`, body);
        api.get('/api/separation/' + selSep.id).then(d => setSepDetail(d));
        notify(`Moved to ${target}`, { title: "Saved", variant: "success" });
        if (afterTab) setSepTab(afterTab);
      } catch (e) {
        // Offer the force path when the block is one HR can waive
        const msg = e.message || 'Status change failed';
        if (/asset\(s\) still assigned|clearance not yet Completed|FnF is|force:true/i.test(msg) && can('separation', 'lifecycle', null, 'edit')) {
          if (await confirmDialog(msg + '\n\nForce-close anyway? (admin/HR/Centre HR only, requires a written reason)')) {
            setAdvBusy(false);
            await doAdvance(target, true, afterTab);
            return;
          }
        }
        notify(msg, { title: "Error", variant: "error" });
      } finally { setAdvBusy(false); }
    };

    // Approve/Reject at the Manager → HR chain, mirroring the backend's
    // exact role gate at each stage (server/routes/separation.js
    // PUT /:id/approve) — shown right here on Overview so an approver
    // doesn't have to separately open "Manage Requests" to act on a record
    // they've already opened. A manager whose system_role is plain
    // 'employee' reaches this same detail view via their "Your Team's
    // Exits" panel, so this can't be gated behind the generic `can()`
    // permission system the rest of Lifecycle Controls uses.
    const isTheirManager = myEmployeeId === sd.manager_id;
    const canApproveHere = cur === 'Pending Manager Approval' ? (isTheirManager || can('separation', 'manage_requests', null, 'edit'))
                          : cur === 'Pending HR Approval' ? (can('separation', 'manage_requests', null, 'edit') && can('separation', 'lifecycle', null, 'view'))
                          : false;
    const doApproveReject = async (action) => {
      if (action === 'approve' && cur === 'Pending HR Approval') { setShowHrApprovalModal(true); return; }
      setApproveBusy(true);
      try {
        await api.put(`/api/separation/${selSep.id}/approve`, { action });
        api.get('/api/separation/' + selSep.id).then(d => setSepDetail(d));
        notify(action === 'approve' ? 'Approved' : 'Rejected', { title: 'Saved', variant: 'success' });
      } catch (e) { notify(e.message || `Failed to ${action}`, { title: 'Error', variant: 'error' }); }
      finally { setApproveBusy(false); }
    };

    return (
      <div>
        <Bt v="ghost" s="sm" icon={ChevronLeft} onClick={() => { setSelSep(null); setSepDetail(null); }} style={{ marginBottom: 12 }}>Back to Separations</Bt>

        <Cd style={{ marginBottom: 14, padding: 20 }}>
          <div className="flex items-center gap-[14px] flex-wrap">
            <Av nm={sd.nm || sd.employee_name || "?"} sz={52} rn={K.r} src={sd.employee_photo} />
            <div className="flex-1 min-w-0">
              <div className="text-[20px] font-extrabold text-t0">{sd.nm || sd.employee_name || "Unknown"}</div>
              <div className="text-[14px] text-t2 mt-[2px]">{sd.dept || sd.department || ""}{sd.designation ? " · " + sd.designation : ""}{sd.employee_id ? " · " + sd.employee_id : ""}</div>
              <div className="flex gap-[6px] mt-[6px] flex-wrap">
                <Bg c={sepTypeColors[sd.reason || sd.separation_type] || K.a}>{sd.reason || sd.separation_type || "Resignation"}</Bg>
                <Bg c={sepStatusColors[sd.status] || K.bl} dot>{sd.status || "In Progress"}</Bg>
              </div>
            </div>
          </div>
        </Cd>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-[10px] mb-[14px]">
          <Cd style={{ padding: 12 }}><div className="text-center"><div className="text-[12px] text-t3 uppercase tracking-[0.06em] mb-1">Resignation Date</div><div className="text-[15px] font-bold text-t0">{fmtDate(sd.resignation_date || sd.initiated || sd.created_at)}</div></div></Cd>
          <Cd style={{ padding: 12 }}><div className="text-center"><div className="text-[12px] text-t3 uppercase tracking-[0.06em] mb-1">Last Working Day</div><div className="text-[15px] font-bold text-t0">{fmtDate(sd.last || sd.last_working_day)}</div></div></Cd>
          <Cd style={{ padding: 12 }}><div className="text-center"><div className="text-[12px] text-t3 uppercase tracking-[0.06em] mb-1">Notice Remaining</div><div className="text-[15px] font-bold" style={{ color: sd.status === "Done" || sd.status === "Completed" ? K.g : K.a }}>{noticeDaysLeft(sd.last || sd.last_working_day)}</div></div></Cd>
          <Cd style={{ padding: 12 }}><div className="text-center"><div className="text-[12px] text-t3 uppercase tracking-[0.06em] mb-1">FnF Due Date</div><div className="text-[15px] font-bold text-t0">{fmtDate(fnfObj.due_date || sd.fnf_due_date)}</div></div></Cd>
        </div>

        <div className="flex gap-0 mb-[14px] border-b border-b0 flex-wrap whitespace-nowrap">
          {[{ k: "overview", l: "Overview", ic: Home }, { k: "checklist", l: "Checklist", ic: ClipboardList }, { k: "clearances", l: "Clearances", ic: ShieldCheck }, { k: "assets", l: "Assets", ic: Monitor }, { k: "interview", l: "Exit Interview", ic: MessageSquare }, { k: "fnf", l: "FnF Settlement", ic: Wallet }].map(t => (
            <button
              key={t.k}
              onClick={() => setSepTab(t.k)}
              className={`flex items-center gap-[5px] px-4 py-2 bg-transparent border-0 text-[14px] cursor-pointer ${sepTab === t.k ? "border-b-2 border-p text-p font-semibold" : "border-b-2 border-transparent text-t2 font-medium"}`}
            ><t.ic size={13} />{t.l}</button>
          ))}
        </div>

        {sepTab === "overview" && <div>
          {canApproveHere && (
            <Cd style={{ marginBottom: 14, borderLeft: `3px solid ${K.a}` }}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="text-[14px] font-bold text-t0">Awaiting your approval — {cur}</div>
                  <div className="text-[14px] text-t3 mt-[2px]">
                    {cur === 'Pending HR Approval'
                      ? "Approving here requires a note, and lets you confirm or adjust the last working date."
                      : "Approve to move this to the next stage, or reject to end it."}
                  </div>
                </div>
                <div className="flex gap-2 flex-wrap">
                  <Bt v="success" s="sm" icon={Check} disabled={approveBusy} onClick={() => doApproveReject('approve')}>{approveBusy ? "Working…" : "Approve"}</Bt>
                  <Bt v="danger" s="sm" icon={X} disabled={approveBusy} onClick={async () => {
                    if (!(await confirmDialog(`Reject ${sd.nm || sd.employee_name || 'this'}'s resignation? This ends the request — they'd need to submit a fresh one.`))) return;
                    doApproveReject('reject');
                  }}>Reject</Bt>
                </div>
              </div>
            </Cd>
          )}
          <Cd style={{ marginBottom: 14 }}>
            <Sec icon={TrendingUp} title="Exit Progress" />
            <div className="flex items-center gap-0 mb-4 overflow-x-auto">
              {sepStages.map((st, i) => <Fragment key={st}>
                <div className="flex flex-col items-center gap-1 flex-shrink-0">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center text-[14px] font-bold" style={{ background: i <= currentStage ? K.p : K.bg2, border: `2px solid ${i <= currentStage ? K.p : K.b1}`, color: i <= currentStage ? "#fff" : K.t3 }}>{i < currentStage ? <Check size={14} /> : i + 1}</div>
                  <div className="text-[12px] text-center w-[70px] whitespace-nowrap" style={{ color: i <= currentStage ? K.t0 : K.t3, fontWeight: i === currentStage ? 700 : 400 }}>{st}</div>
                </div>
                {i < sepStages.length - 1 && <div className="flex-1 h-[2px] min-w-[20px] -mt-4" style={{ background: i < currentStage ? K.p : K.b1 }} />}
              </Fragment>)}
            </div>
          </Cd>
          <Cd style={{ marginBottom: 14 }}>
            <Sec icon={ClipboardList} title={sd.separation_type === 'Termination' ? 'Termination Details' : 'Resignation Details'} />
            {(() => {
              const F = ({ l, v }) => (
                <div className="flex justify-between gap-3 py-[9px] border-b border-b0 last:border-0">
                  <span className="text-[14px] text-t3 flex-shrink-0">{l}</span>
                  <span className="text-[14px] font-semibold text-t0 text-right">{v ?? "—"}</span>
                </div>
              );
              const isTermination = sd.separation_type === 'Termination';
              return (
                <div>
                  <F l="Reason of Leaving" v={sd.reason_category} />
                  {isTermination && <F l="Termination Type" v={sd.termination_type} />}
                  {isTermination && <F l="FNF Eligibility" v={sd.fnf_eligibility} />}
                  {!isTermination && sd.employee_requested_date && <F l="Employee Requested Date" v={fmtDate(sd.employee_requested_date)} />}
                  {isTermination && <F l="Login Disable Date" v={fmtDate(sd.login_disable_date)} />}
                  <F l="Replacement Required" v={sd.replacement_required || "Decide later"} />
                  <F l="Attachment" v={sd.attachment_name ? <span className="text-bl underline cursor-pointer" onClick={() => window.open(BASE + '/' + sd.attachment_path, '_blank')}>{sd.attachment_name}</span> : "—"} />
                  <div className="py-[9px]">
                    <div className="text-[14px] text-t3 mb-[4px]">Explanation</div>
                    <div className="text-[14px] text-t0 whitespace-pre-wrap">{sd.reason_detail || "—"}</div>
                  </div>
                </div>
              );
            })()}
          </Cd>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-[10px]">
            <Cd style={{ padding: 14 }}><div className="text-center"><div className="text-[22px] font-extrabold text-a">{(() => {
              // Defaults to the employee's contractual notice_period_days
              // (30 if unset), but once HR changes the release date, show
              // the ACTUAL gap between resignation date and the (possibly
              // early/extended) last working day instead — so this tile
              // always matches what "Notice Remaining" is counting down to.
              const start = sd.resignation_date || sd.initiated || sd.created_at;
              const end = sd.last || sd.last_working_day;
              const standard = sd.notice || sd.notice_period_days || 30;
              if (!start || !end) return standard;
              const actual = Math.round((new Date(end) - new Date(start)) / 86400000);
              return actual >= 0 ? actual : standard;
            })()}d</div><div className="text-[14px] text-t2 mt-[2px]">Notice Period</div><div className="text-[12px] text-t3 mt-[2px]">{(() => {
              const left = noticeDaysLeft(sd.last || sd.last_working_day);
              return left === '--' ? '' : left === 'Done' ? 'Done' : `${left} left`;
            })()}</div></div></Cd>
            <Cd style={{ padding: 14 }}><div className="text-center"><div className="text-[22px] font-extrabold text-p">{clPct}%</div><div className="text-[14px] text-t2 mt-[2px]">Checklist Done</div><Pr value={clPct} c={K.p} style={{ marginTop: 6 }} /></div></Cd>
            <Cd style={{ padding: 14 }}><div className="text-center"><div className="text-[22px] font-extrabold text-g">{assetsReturned}/{assets.length}</div><div className="text-[14px] text-t2 mt-[2px]">Assets Returned</div></div></Cd>
            <Cd style={{ padding: 14 }}><div className="text-center"><div className="text-[22px] font-extrabold text-tl">{fmtCur(netPayable)}</div><div className="text-[14px] text-t2 mt-[2px]">Net FnF</div></div></Cd>
          </div>
          {can('separation', 'lifecycle', null, 'edit') && (() => {
            // Explicit forward-status controls. Without these, HR could
            // never move the record past 'Notice Period' via the UI, so
            // the auto-generation hooks on 'Exit In Progress' + 'Completed'
            // (Relieving Letter / Experience Letter) never fired for a
            // normal exit — that's why Neha Nair's docs were empty.
            // `cur`/`next`/`doAdvance` are hoisted above (outside this tab's
            // JSX) so Clearances/Assets/Exit Interview/FnF can each render
            // their own "Continue" control using the same logic.
            return (
              <Cd style={{ marginTop: 12, padding: 12 }}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="text-[14px] font-bold text-t0">Lifecycle Controls</div>
                    <div className="text-[14px] text-t3 mt-[2px]">
                      {next
                        ? (next.to === 'Completed'
                            ? <>Current stage: <b className="text-t1">{cur}</b>. {next.note}</>
                            : <>Current stage: <b className="text-t1">{cur}</b>. Go to <b className="text-t1">{next.to === 'Exit In Progress' ? 'Clearances' : 'Assets'}</b> to continue the exit process step by step — advancing straight from here is no longer an option.</>)
                        : <>No further forward transition from <b className="text-t1">{cur}</b>.</>}
                    </div>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    {next && (next.to === 'Completed'
                      ? <Bt v="primary" s="sm" icon={ChevronRight} onClick={() => setShowMarkCompletedModal(true)}>{next.label}</Bt>
                      : <Bt v="primary" s="sm" icon={ChevronRight} onClick={() => setSepTab(next.to === 'Exit In Progress' ? 'clearances' : 'assets')}>
                          {next.to === 'Exit In Progress' ? 'Go to Clearances' : 'Go to Assets'}
                        </Bt>
                    )}
                    {cur && !['Completed', 'Revoked', 'Rejected', 'Cancelled'].includes(cur) && (
                      <Bt v="secondary" s="sm" icon={Timer} onClick={() => setShowReleaseDateModal(true)}>Change Release Date</Bt>
                    )}
                    {cur && cur !== 'Completed' && cur !== 'Revoked' && (
                      <Bt v="danger" s="sm" icon={X} disabled={advBusy} onClick={async () => {
                        if (!(await confirmDialog(`Revoke this separation? The employee stays active.`))) return;
                        setAdvBusy(true);
                        try {
                          await api.put(`/api/separation/${selSep.id}/status`, { status: 'Revoked' });
                          api.get('/api/separation/' + selSep.id).then(d => setSepDetail(d));
                          notify('Separation revoked', { title: 'Saved', variant: 'success' });
                        } catch (e) { notify(e.message || 'Revoke failed', { title: 'Error', variant: 'error' }); }
                        finally { setAdvBusy(false); }
                      }}>{advBusy ? "Working…" : "Revoke"}</Bt>
                    )}
                  </div>
                </div>
              </Cd>
            );
          })()}
          {sd.status === 'Completed' && (
            <div className={`mt-3 p-3 rounded-lg flex items-start gap-2 flex-wrap ${sd.login_active ? 'bg-ab border border-ad' : 'bg-gb border border-gd'}`}>
              <ShieldCheck size={14} color={sd.login_active ? K.a : K.g} className="mt-[2px]" />
              <div className="text-[14px] text-t1 flex-1 min-w-[200px]">
                {sd.login_active
                  ? <><span className="font-semibold">HRMS access kept active.</span> HR chose not to revoke this employee's login when marking the separation Completed.</>
                  : <><span className="font-semibold">HRMS access revoked.</span> This employee's login was deactivated when the separation was marked Completed.</>}
              </div>
              {sd.login_active && can('separation', 'lifecycle', null, 'edit') && can('employees', 'settings', 'remove_access', 'edit') && (
                <Bt v="danger" s="sm" disabled={revokeBusy} onClick={async () => {
                  if (!(await confirmDialog(`Remove ${sd.nm || sd.employee_name || 'this employee'}'s HRMS access now?\n\nThis immediately logs them out of every device, blocks sign-in, and resets their password to a random string nobody is given.`))) return;
                  setRevokeBusy(true);
                  try {
                    // The robust path (force-logout + random password), same
                    // one used from the Employees profile — the separation's
                    // own /revoke-access only ever flipped users.is_active,
                    // which didn't invalidate an already-open session.
                    await api.put(`/api/employees/${sd.employee_id}/remove-access`);
                    api.get('/api/separation/' + selSep.id).then(d => setSepDetail(d));
                    notify('HRMS access removed', { title: 'Saved', variant: 'success' });
                  } catch (e) { notify(e.message || 'Failed to remove access', { title: 'Error', variant: 'error' }); }
                  finally { setRevokeBusy(false); }
                }}>{revokeBusy ? "Working…" : "Remove Access"}</Bt>
              )}
            </div>
          )}
          {sd.completion_note && (
            <Cd style={{ marginTop: 12, padding: 12 }}>
              <div className="text-[14px] font-bold text-t0 mb-[6px]">Closing Note</div>
              <div className="text-[14px] text-t1 whitespace-pre-wrap">{sd.completion_note}</div>
            </Cd>
          )}
          {sd.hr_approval_note && (
            <Cd style={{ marginTop: 12, padding: 12 }}>
              <div className="text-[14px] font-bold text-t0 mb-[6px]">HR Approval Note</div>
              <div className="text-[14px] text-t1 whitespace-pre-wrap">{sd.hr_approval_note}</div>
            </Cd>
          )}
          {can('separation', 'lifecycle', 'supporting_docs', 'view') && (
            <Cd style={{ marginTop: 12, padding: 12 }}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="text-[14px] font-bold text-t0">Supporting documents</div>
                  <div className="text-[14px] text-t3 mt-[2px]">Email trails, screenshots, notices or any other record for this exit — most useful for terminations and absconding. Only HR can see these.</div>
                </div>
                {can('separation', 'lifecycle', 'supporting_docs', 'edit') && (
                  <div>
                    <input ref={supDocRef} type="file" multiple className="hidden"
                      accept=".pdf,.png,.jpg,.jpeg,.gif,.webp,.bmp,.doc,.docx,.xls,.xlsx,.csv,.txt,.msg,.eml,.zip,.rar,.7z"
                      onChange={async (e) => {
                        const list = Array.from(e.target.files || []);
                        if (supDocRef.current) supDocRef.current.value = "";
                        if (!list.length) return;
                        if (list.some(f => f.size > 25 * 1024 * 1024)) return notify("A file is over 25 MB", { title: "File too large", variant: "error" });
                        setSupDocBusy(true);
                        try {
                          const fd = new FormData(); list.forEach(f => fd.append("files", f));
                          await api.upload(`/api/separation/${selSep.id}/documents`, fd);
                          notify(`${list.length} file${list.length === 1 ? "" : "s"} added`, { title: "Saved", variant: "success" });
                          api.get('/api/separation/' + selSep.id).then(d => setSepDetail(d));
                        } catch (er) { notify(er.message || "Upload failed", { title: "Error", variant: "error" }); }
                        finally { setSupDocBusy(false); }
                      }} />
                    <Bt s="sm" icon={Plus} disabled={supDocBusy} onClick={() => supDocRef.current && supDocRef.current.click()}>{supDocBusy ? "Uploading…" : "Add documents"}</Bt>
                  </div>
                )}
              </div>
              <div className="text-[12px] text-t3 mt-1">{SEP_FILE_HINT}</div>
              {(raw.supporting_documents || []).length > 0 ? (
                <div className="mt-3 flex flex-col gap-[6px]">
                  {raw.supporting_documents.map(d => (
                    <div key={d.id} className="flex items-center justify-between gap-3 px-3 py-2 bg-bg2 rounded-lg">
                      <div className="min-w-0">
                        <div className="text-[14px] font-semibold text-t0 truncate">{d.file_name}</div>
                        <div className="text-[12px] text-t3">{fmtSize(d.file_size)}{d.file_size ? " · " : ""}{fmtDate(d.uploaded_at)}{d.uploaded_by ? " · " + d.uploaded_by : ""}</div>
                      </div>
                      <div className="flex items-center gap-1">
                        <Bt v="ghost" s="xs" icon={Download} onClick={() => window.open(uploadUrl(d.file_path), '_blank')}>Download</Bt>
                        {can('separation', 'lifecycle', 'supporting_docs', 'edit') && (
                          <Bt v="danger" s="xs" icon={X} onClick={async () => {
                            if (!(await confirmDialog(`Remove "${d.file_name}"?`))) return;
                            try { await api.del(`/api/separation/${selSep.id}/documents/${d.id}`); api.get('/api/separation/' + selSep.id).then(x => setSepDetail(x)); }
                            catch (er) { notify(er.message || "Could not remove", { title: "Error", variant: "error" }); }
                          }} />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-[14px] text-t3 mt-3">No supporting documents added yet.</div>
              )}
            </Cd>
          )}
          {(can('separation', 'lifecycle', 'regenerate_docs', 'edit') || can('separation', 'lifecycle', 'send_exit_letters', 'edit')) && (
            <Cd style={{ marginTop: 12, padding: 12 }}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="text-[14px] font-bold text-t0">Exit Documents</div>
                  <div className="text-[14px] text-t3 mt-[2px]">
Documents are filed here as each stage is reached. The Relieving Letter and the Experience Letter are never emailed on their own — use the two buttons below to send each one to the employee when you are ready.</div>
                </div>
                {can('separation', 'lifecycle', 'regenerate_docs', 'edit') && <Bt v="secondary" s="sm" icon={RefreshCw} disabled={regenBusy} onClick={async () => {
                  setRegenBusy(true);
                  try {
                    const r = await api.post(`/api/separation/${selSep.id}/regenerate-docs`, {});
                    const gen = (r?.generated || []).length;
                    const failed = (r?.failed || []).length;
                    notify(`Regenerated ${gen} document${gen === 1 ? '' : 's'}${failed ? ` (${failed} failed — check server log)` : ''}.`, { title: 'Saved', variant: 'success' });
                    api.get('/api/separation/' + selSep.id).then(d => setSepDetail(d));
                  } catch (e) { notify(e.message || 'Regenerate failed', { title: 'Error', variant: 'error' }); }
                  finally { setRegenBusy(false); }
                }}>{regenBusy ? "Regenerating…" : "Regenerate Exit Docs"}</Bt>}
              </div>
              {can('separation', 'lifecycle', 'send_exit_letters', 'edit') && (
                <>
                <div className="mt-3 px-3 py-2 rounded-lg flex items-center justify-between gap-2 flex-wrap" style={{ background: (raw.relieving_letter_released ? K.g : K.a) + "14" }}>
                  <div className="text-[14px] text-t0"><b>Relieving Letter for this exit:</b> {raw.relieving_letter_released ? "will be released" : "NOT released"}
                    <span className="text-[12px] text-t3"> — {raw.relieving_letter_decision ? "your choice" : (["Termination", "Absconding"].includes(raw.separation_type) ? "default for a " + String(raw.separation_type).toLowerCase() : "default")}</span></div>
                  <Bt v="secondary" s="xs" disabled={decisionBusy} onClick={async () => {
                    const next = raw.relieving_letter_released ? "withhold" : "release";
                    if (!(await confirmDialog(next === "release" ? "Release a Relieving Letter for this exit? It will be prepared when the exit reaches Exit In Progress, and you can email it with the button below." : "Do not release a Relieving Letter for this exit? The email button is switched off and none is prepared."))) return;
                    setDecisionBusy(true);
                    try { await api.put(`/api/separation/${selSep.id}/relieving-decision`, { decision: next }); api.get('/api/separation/' + selSep.id).then(d => setSepDetail(d)); notify(next === "release" ? "Relieving Letter will be released" : "Relieving Letter will not be released", { title: "Saved", variant: "success" }); }
                    catch (er) { notify(er.message || "Failed", { title: "Error", variant: "error" }); }
                    finally { setDecisionBusy(false); }
                  }}>{raw.relieving_letter_released ? "Do not release" : "Release it"}</Bt>
                </div>
                <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {[['relieving', 'Relieving Letter', raw.relieving_letter_sent_at, raw.relieving_letter_sent_by], ['experience', 'Experience Letter', raw.experience_letter_sent_at, raw.experience_letter_sent_by]].map(([kind, label, at, by]) => (
                    <div key={kind} className="px-3 py-2 bg-bg2 rounded-lg flex items-center justify-between gap-2 flex-wrap">
                      <div className="min-w-0">
                        <div className="text-[14px] font-semibold text-t0">{label}</div>
                        <div className="text-[12px] text-t3">{at ? `Emailed ${fmtDate(at)}${by ? ' by ' + by : ''}` : 'Not sent to the employee yet'}</div>
                      </div>
                      <Bt s="xs" icon={Mail} disabled={sendLetterBusy === kind || (kind === 'relieving' && !raw.relieving_letter_released)} onClick={async () => {
                        if (!(await confirmDialog(`Email the ${label} to ${raw.employee_name || 'the employee'} now? ${sd.separation_type === 'Termination' ? 'This is a termination — please make sure this should go out. ' : ''}You will be copied.`))) return;
                        setSendLetterBusy(kind);
                        try {
                          const r = await api.post(`/api/separation/${selSep.id}/send-letter`, { type: kind });
                          if (r?.sent) notify(`${label} emailed`, { title: 'Sent', variant: 'success' });
                          else notify(r?.reason === 'globally_disabled' || r?.reason === 'category_disabled' || r?.reason === 'smtp_disabled' ? 'Email sending is switched off on this server, so nothing was sent.' : r?.reason === 'doc_type_disabled' ? 'This letter is muted in Settings → Email, so nothing was sent.' : `Not sent (${r?.reason || 'unknown reason'}).`, { title: 'Not sent', variant: 'error' });
                          api.get('/api/separation/' + selSep.id).then(d => setSepDetail(d));
                        } catch (e) { notify(e.message || 'Could not send', { title: 'Error', variant: 'error' }); }
                        finally { setSendLetterBusy(null); }
                      }}>{sendLetterBusy === kind ? 'Sending…' : at ? 'Send again' : `Email ${label}`}</Bt>
                    </div>
                  ))}
                </div>
                </>
              )}
              {(raw.exit_documents || []).length > 0 ? (
                <div className="mt-3 flex flex-col gap-[6px]">
                  {raw.exit_documents.map(d => (
                    <div key={d.id} className="flex items-center justify-between gap-3 px-3 py-2 bg-bg2 rounded-lg">
                      <div className="min-w-0">
                        <div className="text-[14px] font-semibold text-t0">{d.doc_type}</div>
                        <div className="text-[12px] text-t3">{fmtDate(d.uploaded_at)}</div>
                      </div>
                      <Bt v="ghost" s="xs" icon={Download} onClick={() => window.open(uploadUrl(d.file_path), '_blank')}>Download</Bt>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-[14px] text-t3 mt-3">No exit documents generated yet.</div>
              )}
            </Cd>
          )}
        </div>}

        {sepTab === "checklist" && <div>
          <Sec icon={ClipboardList} title={`Checklist (${doneCl}/${totalCl})`} />
          <div className="flex flex-col gap-[14px]">
            {Object.entries(clGroups).map(([cat, items]) => (
              <Cd key={cat} style={{ padding: 14 }}>
                <div className="text-[13px] font-bold text-t0 uppercase tracking-wide mb-[8px]">{cat}</div>
                <div className="flex flex-col gap-[2px]">
                  {items.map(item => (
                    <label key={item.id} className="flex items-center gap-[10px] py-[6px] cursor-pointer">
                      <input
                        type="checkbox"
                        checked={!!item.is_completed}
                        disabled={!can('separation', 'clearances', null, 'edit')}
                        onChange={() => toggleChecklistItem(item)}
                        className="w-[16px] h-[16px] accent-p cursor-pointer flex-shrink-0"
                      />
                      <span className={`text-[14px] flex-1 ${item.is_completed ? 'text-t3 line-through' : 'text-t0'}`}>{item.item_name}</span>
                      {item.is_completed && item.completed_by && (
                        <span className="text-[12px] text-t3 flex-shrink-0">{item.completed_by} · {fmtDate(item.completed_date)}</span>
                      )}
                    </label>
                  ))}
                </div>
              </Cd>
            ))}
            {allClItems.length === 0 && (
              <Cd><div className="text-center p-[20px] text-t3 text-[14px]">No checklist items for this separation.</div></Cd>
            )}
          </div>
        </div>}

        {sepTab === "clearances" && <div>
          <Sec icon={ShieldCheck} title="Clearances" />
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-[10px]">
            {CLEARANCE_TYPES.map(t => {
              const c = raw.clearances?.[t.k];
              return (
                <Cd key={t.k} style={{ padding: 14 }}>
                  <div className="flex items-center gap-2 mb-2"><t.ic size={16} color={K.p} /><span className="text-[14px] font-bold text-t0">{t.l}</span></div>
                  <Bg c={c?.status === 'Completed' ? K.g : K.a} sm dot>{c?.status || 'Open'}</Bg>
                  <div className="text-[12px] text-t3 mt-2">{c?.completed_on ? `Completed ${fmtDate(c.completed_on)}` : (c?.due_date ? `Due ${fmtDate(c.due_date)}` : 'No due date set')}</div>
                  <Bt v="secondary" s="sm" full style={{ marginTop: 10 }} onClick={() => setOpenClrType(t.k)} disabled={!c}>{c ? 'Open' : 'Not available'}</Bt>
                </Cd>
              );
            })}
          </div>
          {openClrType && raw.clearances?.[openClrType] && (
            <ClearanceDetailModal
              type={openClrType}
              row={{ ...raw.clearances[openClrType], employee_id: sd.employee_id, employee_name: sd.nm || sd.employee_name, last_working_day: sd.last || sd.last_working_day }}
              canEdit={can('separation', 'clearances', null, 'edit')}
              onClose={() => setOpenClrType(null)}
              onSaved={afterClearanceSave}
            />
          )}
          {can('separation', 'lifecycle', null, 'edit') && cur === 'Notice Period' && next && (
            <Cd style={{ marginTop: 14, padding: 14 }}>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="text-[14px] text-t2">Clearances don't need to be closed to move on — HR can come back and finish them later.</div>
                <Bt v="primary" s="sm" icon={ChevronRight} disabled={advBusy} onClick={() => doAdvance(next.to, false, 'assets')}>{advBusy ? "Updating…" : "Continue to Assets"}</Bt>
              </div>
            </Cd>
          )}
        </div>}

        {sepTab === "assets" && <div>
          {/* Section 1 — live join with the core asset module. These are the
              real items the employee is currently holding (laptops, mobiles,
              cards, vehicles…). Separation page is just a view onto the
              assignment lifecycle; actions call the same /initiate-return
              and /complete-return endpoints used in the Assets module. */}
          <div className="flex justify-between items-center mb-3">
            <Sec icon={Monitor} title={`Issued Assets (${trackedAssets.length})`} />
            {trackedOpen > 0 && can('separation', 'clearances', null, 'edit') && (
              <Bt v="danger" s="sm" icon={RefreshCw} disabled={bulkReturnBusy}
                  onClick={async () => {
                    if (!(await confirmDialog(`Initiate return on all ${trackedOpen} outstanding asset(s) for this employee? Pending acknowledgements will be cancelled; active items go to inspection queue.`))) return;
                    setBulkReturnBusy(true);
                    try {
                      const r = await api.post("/api/separation/" + selSep.id + "/initiate-all-returns");
                      afterAssetChange();
                      notify(`Done. ${r.initiated} returns initiated, ${r.cancelled} pending assignments cancelled.${r.already_pending_return ? ` ${r.already_pending_return} already in return.` : ''}`, { title: 'Saved', variant: 'success' });
                    } catch (e) { notify(e.message || 'Failed', { title: 'Error', variant: 'error' }); }
                    finally { setBulkReturnBusy(false); }
                  }}>{bulkReturnBusy ? "Processing…" : "Initiate Return on All"}</Bt>
            )}
          </div>
          {trackedAssets.length === 0 ? (
            <Cd style={{ marginBottom: 12 }}>
              <div className="text-center p-[20px] text-t3 text-[14px]">No assets currently issued to this employee.</div>
            </Cd>
          ) : (
            <Cd style={{ marginBottom: 16 }}>
              <Tbl cols={[
                { k: "aid",   l: "Asset ID",  r: r => <span className="font-mono text-[14px] text-t2">{r.assetId}</span> },
                { k: "type",  l: "Type",      r: r => <Bg c={r.catColor || K.p} sm>{r.catName || '—'}</Bg> },
                { k: "item",  l: "Item",      r: r => <span className="text-[14px] font-semibold text-t0">{`${r.brand || ''} ${r.model || ''}`.trim() || r.serial || '—'}</span> },
                { k: "since", l: "Held Since",r: r => <span className="text-[14px] text-t3">{r.assignedDate || '—'}</span> },
                { k: "state", l: "State",     r: r => {
                  const meta = {
                    pending_ack:    { c: '#F0BE2E', l: 'Awaiting Ack' },
                    active:         { c: '#2BD49D', l: 'Active' },
                    pending_return: { c: '#5AA0F0', l: 'Return in Progress' },
                  }[r.state] || { c: K.t3, l: r.state };
                  return <Bg c={meta.c} sm>{meta.l}</Bg>;
                } },
                { k: "act", l: "Action", r: r => {
                  if (!can('separation', 'clearances', null, 'edit')) return null;
                  const rowBusy = assetActBusy === r.assetId;
                  if (r.state === 'pending_ack') {
                    return <Bt v="danger" s="xs" icon={X} disabled={rowBusy} onClick={async () => {
                      if (!(await confirmDialog(`Cancel pending assignment for ${r.assetId}? Asset returns to pool.`))) return;
                      setAssetActBusy(r.assetId);
                      try {
                        await api.post(`/api/assets/${r.assetId}/cancel-assignment`);
                        afterAssetChange();
                        notify(`Assignment for ${r.assetId} cancelled`, { title: "Saved", variant: "success" });
                      } catch (e) { notify(e.message || "Failed to cancel assignment", { title: "Error", variant: "error" }); }
                      finally { setAssetActBusy(null); }
                    }}>{rowBusy ? "Cancelling…" : "Cancel"}</Bt>;
                  }
                  if (r.state === 'active') {
                    return <Bt v="secondary" s="xs" icon={RefreshCw} disabled={rowBusy} onClick={async () => {
                      setAssetActBusy(r.assetId);
                      try {
                        await api.post(`/api/assets/${r.assetId}/initiate-return`);
                        afterAssetChange();
                        notify(`Return initiated for ${r.assetId}`, { title: "Saved", variant: "success" });
                      } catch (e) { notify(e.message || "Failed to initiate return", { title: "Error", variant: "error" }); }
                      finally { setAssetActBusy(null); }
                    }}>{rowBusy ? "Starting…" : "Initiate Return"}</Bt>;
                  }
                  if (r.state === 'pending_return') {
                    return <span className="text-[14px] text-bl">Inspect from Assets</span>;
                  }
                  return null;
                } },
              ]} data={trackedAssets} />
            </Cd>
          )}

          {/* Visible warning when HR is one step from Completed but assets
              are still out — backend also blocks but the early signal here
              prevents the surprise 409. */}
          {trackedOpen > 0 && (
            <div className="mb-4 p-3 rounded-lg bg-ab border border-ad flex items-start gap-2">
              <AlertTriangle size={14} color={K.a} className="mt-[2px]" />
              <div className="text-[14px] text-t1">
                <span className="font-semibold">{trackedOpen} asset{trackedOpen === 1 ? '' : 's'} still outstanding.</span> Separation can't be marked Completed until each is returned and inspected (Assets → asset detail → Complete Return Inspection), or HR explicitly waives via force flag.
              </div>
            </div>
          )}

          {/* Section 2 — legacy free-form items the core asset module doesn't
              track (uniforms, parking pass, keys, books). Kept for that
              long-tail of physical things HR still needs to recover. */}
          <div className="flex justify-between items-center mb-3">
            <Sec icon={Monitor} title="Other Items" />
            <Bt s="sm" icon={Plus} onClick={() => setSepAssetForm(true)}>Add Item</Bt>
          </div>
          {tplDiff && tplDiff.assets_to_add?.length > 0 && can('separation', 'clearances', null, 'edit') && (
            <div className="mb-3 p-3 rounded-lg bg-ab border border-ad flex items-start gap-2">
              <AlertTriangle size={14} color={K.a} className="mt-[2px] flex-shrink-0" />
              <div className="flex-1 text-[14px] text-t1">
                <span className="font-semibold">Asset template has {tplDiff.assets_to_add.length} newer item{tplDiff.assets_to_add.length === 1 ? '' : 's'}</span> not on this exit's list. Sync to add them — existing items are untouched.
              </div>
              <Bt v="primary" s="xs" icon={RefreshCw} disabled={syncBusy}
                  onClick={async () => {
                    if (!(await confirmDialog(`Add ${tplDiff.assets_to_add.length} new asset item(s) from the template?`))) return;
                    setSyncBusy(true);
                    try {
                      const r = await api.post("/api/separation/" + selSep.id + "/sync-from-template", { kinds: ['assets'] });
                      api.get("/api/separation/" + selSep.id).then(d => setSepDetail(d));
                      reloadTplDiff(selSep.id);
                      notify(`Added ${r.added_assets} asset item(s).`, { title: 'Saved', variant: 'success' });
                    } catch (e) { notify(e.message || 'Sync failed', { title: 'Error', variant: 'error' }); }
                    finally { setSyncBusy(false); }
                  }}>{syncBusy ? "Syncing…" : "Sync from Template"}</Bt>
            </div>
          )}
          {assets.length > 0 ? <Tbl cols={[
            { k: "type", l: "Asset Type", r: r => <span className="font-semibold">{r.asset_type || r.type || "Device"}</span> },
            { k: "aid", l: "Asset ID", r: r => <span className="text-[12px] text-t2">{r.asset_id || r.id || "--"}</span> },
            { k: "desc", l: "Description", r: r => <span className="text-[12px]">{r.description || r.name || "--"}</span> },
            { k: "issued", l: "Issued", r: r => <span className="text-[12px]">{fmtDate(r.issued_date || r.assigned_date)}</span> },
            { k: "status", l: "Status", r: r => <Bg c={assetStatusColors[r.status] || K.a} dot sm>{r.status || "Pending Return"}</Bg> },
            { k: "ret", l: "Returned", r: r => <span className="text-[12px]">{fmtDate(r.returned_date)}</span> },
            { k: "cond", l: "Condition", r: r => <span className="text-[12px] text-t2">{r.condition || "--"}</span> },
            { k: "act", l: "Action", r: r => r.status !== "Returned" ? <Sl options={[{ v: "", l: "Update..." }, { v: "Returned", l: "Returned" }, { v: "Damaged", l: "Damaged" }, { v: "Lost", l: "Lost" }, { v: "Waived", l: "Waived" }]} value="" onChange={v => { if (v) api.put("/api/separation/" + selSep.id + "/assets/" + r.id, { status: v }).then(() => { afterAssetChange(); notify(`Asset marked ${v}`, { title: "Saved", variant: "success" }); }).catch(e => notify(e.message || "Failed to update asset", { title: "Error", variant: "error" })); }} /> : null }
          ]} data={assets} /> : <Cd>
            <div className="text-center p-[30px] text-t3">
              <Monitor size={32} color={K.t3} className="mb-2 mx-auto" />
              <div className="text-[14px]">No assets recorded</div>
              <div className="text-[14px] mt-1">Click "Add Asset" to add items for return</div>
            </div>
          </Cd>}
          {sepAssetForm && <FormModal title="Add Asset" fields={[
            { key: "asset_type", label: "Asset Type", type: "select", options: [{ v: "Laptop", l: "Laptop" }, { v: "Mobile", l: "Mobile" }, { v: "ID Card", l: "ID Card" }, { v: "Access Card", l: "Access Card" }, { v: "Parking Pass", l: "Parking Pass" }, { v: "Uniform", l: "Uniform" }, { v: "Keys", l: "Keys" }, { v: "Other", l: "Other" }] },
            { key: "asset_id", label: "Asset ID / Serial No.", placeholder: "LAP-00123" },
            { key: "description", label: "Description", placeholder: "MacBook Pro 14-inch" },
            { key: "issued_date", label: "Issued Date", type: "date" }
          ]} onSubmit={async v => { await api.post("/api/separation/" + selSep.id + "/assets", v); api.get("/api/separation/" + selSep.id).then(d => setSepDetail(d)); notify("Asset added", { title: "Saved", variant: "success" }); }} onClose={() => setSepAssetForm(false)} />}
          {can('separation', 'lifecycle', null, 'edit') && cur === 'Exit In Progress' && next && (
            <Cd style={{ marginTop: 14, padding: 14 }}>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="text-[14px] text-t2">Assets don't all need to be returned yet to move on.</div>
                <Bt v="primary" s="sm" icon={ChevronRight} disabled={advBusy} onClick={() => doAdvance(next.to, false, 'interview')}>{advBusy ? "Updating…" : "Continue to Exit Interview"}</Bt>
              </div>
            </Cd>
          )}
        </div>}

        {sepTab === "interview" && <div>
          <ExitInterviewTab sepId={selSep.id} role={role} can={can} />
          {can('separation', 'lifecycle', null, 'edit') && (
            <Cd style={{ marginTop: 14, padding: 14 }}>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="text-[14px] text-t2">The exit interview isn't required to move on.</div>
                <Bt v="primary" s="sm" icon={ChevronRight} onClick={() => setSepTab('fnf')}>Continue to FnF Settlement</Bt>
              </div>
            </Cd>
          )}
        </div>}

        {sepTab === "fnf" && (() => {
          const fnfCanEdit = can('separation', 'fnf', null, 'edit') && sd.status !== 'Completed';
          const fnfDoc = (raw.exit_documents || []).find(d => d.doc_type === 'Full & Final Settlement');
          const editing = fnfEditVals !== null;
          const EDIT_ROWS = [
            { k: 'basic_dues', l: 'Basic Dues (salary for worked days)' },
            { k: 'leave_encashment', l: 'Leave Encashment' },
            { k: 'bonus', l: 'Bonus / Incentive' },
            { k: 'gratuity', l: 'Gratuity (if eligible)' },
          ];
          const draftEarnings = editing ? EDIT_ROWS.reduce((s, r) => s + (Number(fnfEditVals[r.k]) || 0), 0) : totalEarnings;
          const draftDeductions = editing ? (Number(fnfEditVals.deductions) || 0) : totalDeductions;
          const draftNet = editing ? draftEarnings - draftDeductions : netPayable;

          const saveFnfEdit = async () => {
            setFnfSaveBusy(true);
            try {
              await api.put("/api/separation/" + selSep.id + "/fnf", {
                fnf_basic_dues: Number(fnfEditVals.basic_dues) || 0,
                fnf_leave_encashment: Number(fnfEditVals.leave_encashment) || 0,
                fnf_bonus: Number(fnfEditVals.bonus) || 0,
                fnf_gratuity: Number(fnfEditVals.gratuity) || 0,
                fnf_deductions: Number(fnfEditVals.deductions) || 0,
              });
              api.get("/api/separation/" + selSep.id).then(d => setSepDetail(d));
              notify("FnF amounts saved", { title: "Saved", variant: "success" });
              setFnfEditVals(null);
            } catch (e) { notify(e.message || "Failed to save FnF amounts", { title: "Error", variant: "error" }); }
            finally { setFnfSaveBusy(false); }
          };

          const numInput = (k) => (
            <input type="number" value={fnfEditVals[k]} onChange={e => setFnfEditVals(v => ({ ...v, [k]: e.target.value }))}
                   className="w-[130px] px-[8px] py-[4px] bg-bg2 border border-b0 rounded-[8px] text-[14px] text-t0 text-right outline-none" />
          );

          return (
            <div>
              <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                <Sec icon={Wallet} title="Full & Final Settlement" />
                {fnfCanEdit && !editing && <Bt v="secondary" s="sm" onClick={() => setFnfEditVals({ basic_dues: fnfObj.basic_dues || 0, leave_encashment: fnfObj.leave_encashment || 0, bonus: fnfObj.bonus || 0, gratuity: fnfObj.gratuity || 0, deductions: fnfObj.deductions || 0 })}>Edit Amounts</Bt>}
              </div>
              <Cd style={{ marginBottom: 14 }}>
                <div className="text-[14px] font-bold text-g mb-[10px] uppercase tracking-[0.06em]">Earnings</div>
                {editing ? EDIT_ROWS.map(r => (
                  <div key={r.k} className="flex justify-between items-center py-[5px] border-b border-b0 text-[14px]">
                    <span className="text-t1">{r.l}</span>
                    {numInput(r.k)}
                  </div>
                )) : (earnings.length > 0 ? earnings : [{ label: "Basic Dues (salary for worked days)", amount: fnfObj.basic_dues || 0 }, { label: "Leave Encashment", amount: fnfObj.leave_encashment || 0, sub: fnfObj.leave_days ? fnfObj.leave_days + " days" : null }, { label: "Bonus / Incentive", amount: fnfObj.bonus || 0 }, { label: "Gratuity (if eligible)", amount: fnfObj.gratuity || 0 }]).map((e, i) => (
                  <div key={i} className="flex justify-between py-[5px] border-b border-b0 text-[14px]">
                    <span className="text-t1">{e.label || e.name}{e.sub ? <span className="text-[12px] text-t3"> ({e.sub})</span> : null}</span>
                    <span className="font-semibold text-t0">{fmtCur(e.amount)}</span>
                  </div>
                ))}
                <div className="flex justify-between py-2 text-[14px] font-bold border-t-2 border-dashed border-b1 mt-1">
                  <span className="text-g">Total Earnings</span>
                  <span className="text-g">{fmtCur(draftEarnings)}</span>
                </div>

                <div className="text-[14px] font-bold text-r mb-[10px] mt-4 uppercase tracking-[0.06em]">Deductions</div>
                {editing ? (
                  <div className="flex justify-between items-center py-[5px] border-b border-b0 text-[14px]">
                    <span className="text-t1">Deductions (Notice/Loans/Other)</span>
                    {numInput('deductions')}
                  </div>
                ) : (deductions.length > 0 ? deductions : [{ label: "Notice Period Buyout", amount: fnfObj.notice_buyout || 0 }, { label: "Loan / Advance Recovery", amount: fnfObj.loan_recovery || 0 }, { label: "Other Deductions", amount: fnfObj.other_deductions || 0 }]).map((e, i) => (
                  <div key={i} className="flex justify-between py-[5px] border-b border-b0 text-[14px]">
                    <span className="text-t1">{e.label || e.name}</span>
                    <span className="font-semibold text-r">{fmtCur(e.amount)}</span>
                  </div>
                ))}
                <div className="flex justify-between py-2 text-[14px] font-bold border-t-2 border-dashed border-b1 mt-1">
                  <span className="text-r">Total Deductions</span>
                  <span className="text-r">{fmtCur(draftDeductions)}</span>
                </div>

                <div className="flex justify-between px-[14px] py-3 text-[17px] font-extrabold rounded-[10px] mt-3" style={{ background: `${K.p}10`, border: `2px solid ${K.pb}` }}>
                  <span className="text-t0">NET PAYABLE</span>
                  <span className="text-p">{fmtCur(draftNet)}</span>
                </div>

                <div className="flex gap-4 mt-3 text-[14px] text-t2 flex-wrap">
                  <span>FnF Due: <strong className="text-t0">{fmtDate(fnfObj.due_date || sd.fnf_due_date)}</strong></span>
                  <span>Status: <Bg c={fnfStatusColors[fnfObj.status || sd.fnf_status || "Pending"] || K.a} sm>{fnfObj.status || sd.fnf_status || "Pending"}</Bg></span>
                </div>
              </Cd>
              <div className="flex gap-2 flex-wrap">
                {editing ? (
                  <>
                    <Bt icon={CheckCircle} disabled={fnfSaveBusy} onClick={saveFnfEdit}>{fnfSaveBusy ? "Saving…" : "Save Amounts"}</Bt>
                    <Bt v="secondary" disabled={fnfSaveBusy} onClick={() => setFnfEditVals(null)}>Cancel</Bt>
                  </>
                ) : (
                  <>
                    <Bt icon={RefreshCw} v="secondary" disabled={fnfCalcBusy} onClick={async () => {
                      setFnfCalcBusy(true);
                      try {
                        await api.put("/api/separation/" + selSep.id + "/fnf", { action: "calculate" });
                        api.get("/api/separation/" + selSep.id).then(d => setSepDetail(d));
                        notify("FnF settlement calculated", { title: "Saved", variant: "success" });
                      } catch (e) { notify(e.message || "Failed to calculate FnF", { title: "Error", variant: "error" }); }
                      finally { setFnfCalcBusy(false); }
                    }}>{fnfCalcBusy ? "Calculating…" : "Calculate FnF"}</Bt>
                    {can('separation', 'fnf', 'approve_fnf', 'edit') && <Bt icon={CheckCircle} disabled={fnfApproveBusy} onClick={async () => {
                      setFnfApproveBusy(true);
                      try {
                        await api.put("/api/separation/" + selSep.id + "/fnf/approve", {});
                        api.get("/api/separation/" + selSep.id).then(d => setSepDetail(d));
                        notify("FnF settlement approved", { title: "Saved", variant: "success" });
                      } catch (e) { notify(e.message || "Failed to approve FnF", { title: "Error", variant: "error" }); }
                      finally { setFnfApproveBusy(false); }
                    }}>{fnfApproveBusy ? "Approving…" : "Approve FnF"}</Bt>}
                    <Bt v="secondary" icon={Download} disabled={!fnfDoc} onClick={() => fnfDoc && window.open(BASE + '/' + fnfDoc.file_path, '_blank')}>{fnfDoc ? "Download FnF Report" : "Report available after approval"}</Bt>
                    {cur === 'FnF Processing' && can('separation', 'lifecycle', null, 'edit') && (
                      <Bt icon={ChevronRight} onClick={() => setShowMarkCompletedModal(true)}>Complete Offboarding</Bt>
                    )}
                  </>
                )}
              </div>
            </div>
          );
        })()}

        {/* Rendered here (not inside a single tab's block) so they can be
            triggered from the "Continue" controls on any tab, not just
            Overview's Lifecycle Controls card. */}
        {showMarkCompletedModal && (
          <MarkCompletedModal
            sepId={selSep.id}
            onClose={() => setShowMarkCompletedModal(false)}
            onSaved={() => { setShowMarkCompletedModal(false); api.get('/api/separation/' + selSep.id).then(d => setSepDetail(d)); }}
          />
        )}
        {showReleaseDateModal && (
          <ChangeReleaseDateModal
            sep={sd}
            onClose={() => setShowReleaseDateModal(false)}
            onSaved={() => { setShowReleaseDateModal(false); api.get('/api/separation/' + selSep.id).then(d => setSepDetail(d)); setSepTab("clearances"); }}
          />
        )}
        {showHrApprovalModal && (
          <HrFinalApprovalModal
            sep={sd}
            onClose={() => setShowHrApprovalModal(false)}
            onDone={() => { setShowHrApprovalModal(false); api.get('/api/separation/' + selSep.id).then(d => setSepDetail(d)); }}
          />
        )}
      </div>
    );
  }

  // A plain employee (or recruiter) gets their own separation row PLUS
  // any direct report's (server/routes/separation.js's employee_id OR
  // manager_id scoping — most line managers are plain 'employee', not
  // admin/hr_head/centre_head, and without this they'd have no page at
  // all for approving a report's resignation). The full dashboard below
  // (company-wide KPI cards, type tabs, Export Exit Report, ...) is still
  // HR/admin/centre_head-only chrome — this stays a small personal panel
  // plus, when relevant, a short approval queue.
  if (["employee", "recruiter"].includes(role)) {
    const mine = myEmployeeId ? SP.find(s => s.employee_id === myEmployeeId) : SP[0];
    const reports = myEmployeeId ? SP.filter(s => s.employee_id !== myEmployeeId) : [];
    const doReportAct = async (r, action) => {
      setReportActBusy(r.id);
      try {
        await api.put(`/api/separation/${r.id}/approve`, { action });
        notify(`Resignation ${action === "approve" ? "approved" : "rejected"}`, { title: "Saved", variant: "success" });
        onRefresh?.();
      } catch (e) { notify(e.message || `Failed to ${action}`, { title: "Error", variant: "error" }); }
      finally { setReportActBusy(null); }
    };
    return (
      <div>
        <div className="flex justify-between items-end mb-4 flex-wrap gap-2">
          <div><h1 className="m-0 text-2xl font-extrabold text-t0">Offboarding & Exit</h1><p className="mt-[3px] mb-0 text-t2 text-[14px]">Your resignation / exit status.</p></div>
          {!mine && <Bt icon={LogOut} v="danger" onClick={() => setShowForm("resign")}>Submit Resignation</Bt>}
        </div>
        {mine ? (
          <Cd onClick={() => setSelSep(mine)}>
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <div className="text-[16px] font-bold text-t0">{mine.reason || mine.separation_type || "Resignation"}</div>
                <div className="text-[13px] text-t3 mt-1">Last day: {fmtDate(mine.last || mine.last_working_day)} · Notice: {mine.notice || mine.notice_period_days || 30}d</div>
              </div>
              <Bg c={sepStatusColors[mine.status] || K.bl} dot>{mine.status || "In Progress"}</Bg>
            </div>
            <div className="mt-3 text-[13px] text-p font-semibold">View details →</div>
          </Cd>
        ) : (
          <Cd>
            <div className="text-center p-[40px] text-t3">
              <LogOut size={32} className="mx-auto mb-2" />
              <div className="text-[15px] font-semibold text-t1">You haven't started an exit process</div>
              <div className="text-[14px] mt-1">If you're planning to resign, use the button above to submit your resignation.</div>
            </div>
          </Cd>
        )}
        {reports.length > 0 && (
          <div className="mt-4">
            <div className="text-[15px] font-bold text-t0 mb-2">Your Team's Exits</div>
            <div className="flex flex-col gap-2">
              {reports.map(r => {
                const pendingMine = r.status === "Pending Manager Approval";
                return (
                  <Cd key={r.id}>
                    <div className="flex items-center justify-between flex-wrap gap-3">
                      <div className="min-w-0 cursor-pointer flex-1" onClick={() => setSelSep(r)}>
                        <div className="text-[15px] font-bold text-t0">{r.employee_name}</div>
                        <div className="text-[13px] text-t3 mt-1">{r.separation_type} · Last day: {fmtDate(r.last_working_day)}</div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Bg c={sepStatusColors[r.status] || K.bl} dot>{r.status}</Bg>
                        {pendingMine && (
                          <div className="flex gap-[6px]">
                            <Bt v="success" s="xs" icon={Check} disabled={reportActBusy === r.id} onClick={() => doReportAct(r, "approve")}>Approve</Bt>
                            <Bt v="danger" s="xs" icon={X} disabled={reportActBusy === r.id} onClick={() => doReportAct(r, "reject")}>Reject</Bt>
                          </div>
                        )}
                      </div>
                    </div>
                  </Cd>
                );
              })}
            </div>
          </div>
        )}
        {showForm === "resign" && <FormModal title="Submit Resignation" fields={[
          { key: "reason_category", label: "Reason of Leaving *", type: "select",
            options: [{ v: "", l: "Select Reason..." }, ...sepOptions.reason_categories.map(r => ({ v: r, l: r }))] },
          { key: "reason_detail", label: "Explanation (optional)", placeholder: "Please share more about your decision..." },
          { key: "preferred_last_day", label: "Employee requested date", type: "date" },
          { key: "replacement_required", label: "Replacement required", type: "select",
            options: [{ v: "", l: "Decide later" }, { v: "Yes", l: "Yes" }, { v: "No", l: "No" }] },
        ]} onSubmit={async v => { if (!v.reason_category) throw new Error("Please select a reason"); await api.post("/api/separation/resign", v); onRefresh?.(); notify("Resignation submitted", { title: "Saved", variant: "success" }); }} onClose={() => setShowForm(null)} />}
      </div>
    );
  }

  const activeExits = SP.filter(s => s.status !== "Done" && s.status !== "Completed").length;
  const inNotice = SP.filter(s => (s.status || "").includes("Notice")).length;
  const fnfPending = SP.filter(s => s.fnf === "Pending" || s.fnf === "In Progress" || (s.status || "").includes("FnF")).length;
  const completedExits = SP.filter(s => s.status === "Done" || s.status === "Completed").length;
  const noticeGroup = SP.filter(s => (s.status || "").includes("Notice"));
  const avgNoticeDays = noticeGroup.length > 0 ? Math.round(noticeGroup.reduce((s, r) => s + (r.notice || r.notice_period_days || 30), 0) / noticeGroup.length) : 0;

  const exportSepCsv = () => {
    const header = ["Employee", "Employee ID", "Department", "Type", "Last Day", "Notice", "Status", "FnF"];
    const rows = SP.map(r => [r.nm || r.employee_name, r.employee_id || "", r.dept || r.department || "", r.reason || r.separation_type || "", fmtDate(r.last || r.last_working_day), (r.notice || r.notice_period_days || 30) + "d", r.status || "", r.fnf || r.fnf_status || ""]);
    const csv = [header, ...rows].map(row => row.map(v => `"${String(v ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `separations_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <div className="flex justify-between items-end mb-4 flex-wrap gap-2">
        <div><h1 className="m-0 text-2xl font-extrabold text-t0">Offboarding & Exit</h1><p className="mt-[3px] mb-0 text-t2 text-[14px]">{SP.length} total separations · Track notice periods, asset clearances, and full &amp; final settlements</p></div>
        <div className="flex gap-[6px]">
          <Bt v="secondary" icon={Download} onClick={exportSepCsv}>Export Exit Report</Bt>
          {can('separation', 'manage_requests', null, 'edit') && <Bt icon={Plus} onClick={() => setShowForm("separation")}>Initiate Exit</Bt>}
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        {[
          { icon: LogOut,      label: "Active Exits",     value: activeExits,     c: K.r,  sub: "Across all departments" },
          { icon: Timer,       label: "In Notice Period", value: inNotice,        c: K.a,  sub: avgNoticeDays ? `Avg. notice: ${avgNoticeDays} days` : "No one on notice" },
          { icon: Wallet,      label: "FnF Pending",      value: fnfPending,      c: K.bl, sub: "Awaiting final clearance" },
          { icon: CheckCircle, label: "Completed",        value: completedExits,  c: K.g,  sub: "Archived this fiscal" },
        ].map(s => (
          <div key={s.label} className="bg-s rounded-xl p-5 border border-b0 flex items-center gap-4 shadow-[0_2px_4px_rgba(0,0,0,0.02)]" style={{ borderLeft: `4px solid ${s.c}` }}>
            <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: `${s.c}14`, border: `1px solid ${s.c}2a`, color: s.c }}>
              <s.icon size={22} />
            </div>
            <div className="min-w-0">
              <div className="flex items-baseline gap-2">
                <span className="text-2xl font-extrabold text-t0 leading-none">{s.value}</span>
                <span className="text-[10px] font-bold uppercase tracking-wide" style={{ color: s.c }}>{s.label}</span>
              </div>
              <p className="text-[11px] text-t3 mt-1 font-medium truncate">{s.sub}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="flex gap-0 mb-4 border-b border-b0 flex-wrap whitespace-nowrap">
        {[
          { k: "requests", l: "Manage Requests", ic: ClipboardList },
          { k: "track", l: "Track Offboarding", ic: Activity },
        ].map(t => (
          <button key={t.k} onClick={() => setMgmtTab(t.k)}
            className={`flex items-center gap-[5px] px-4 py-2 bg-transparent border-0 text-[14px] cursor-pointer ${mgmtTab === t.k ? "border-b-2 border-p text-p font-semibold" : "border-b-2 border-transparent text-t2 font-medium"}`}
          ><t.ic size={13} />{t.l}</button>
        ))}
      </div>

      {mgmtTab === "requests" && <TrackOffboardingView role={role} SP={SP} employees={employees} options={sepOptions} onOpen={setSelSep} onRefresh={onRefresh} can={can} />}
      {mgmtTab === "track" && <ManageRequestsView SP={SP} onOpen={setSelSep} role={role} onRefresh={onRefresh} can={can} />}

      {showForm === "separation" && <InitiateExitModal
        employees={employees}
        options={sepOptions}
        initiateEmpId={initiateEmpId}
        setInitiateEmpId={setInitiateEmpId}
        employeeNoticeDays={employeeNoticeDays}
        onClose={() => { setShowForm(null); setInitiateEmpId(""); }}
        onSubmit={async (v, files) => { const created = await api.post("/api/separation", v); await uploadSepFiles(created?.separation?.id, files); onRefresh?.(); }} />}
    </div>
  );
};
